/**
 * Custom Print Generator - JavaScript
 * Skylit Studio - Print Your Universe
 *
 * Enhanced with Desenio-style features:
 * - Image upload with drag & drop
 * - Quality validation with percentage
 * - Image filters (grayscale, sepia, warm, cool, vivid)
 * - Crop modal with zoom slider, pan, and rotate
 * - Orientation toggle (portrait/landscape)
 * - Size/frame/product selection
 * - Cart submission
 */

(function() {
    'use strict';

    // ============================================
    // Configuration
    // ============================================
    const API_BASE = 'https://admin.skylitstudio.co.uk/';  // Note: data endpoints need api/ prefix
    const ADMIN_BASE = 'https://admin.skylitstudio.co.uk/';
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/tiff'];

    // Font family resolver — accepts both new keys (matching the Google
    // Fonts family name, e.g. "Josefin Sans", "Sacramento") and legacy
    // keys ("modern", "classic", "minimalist") so existing carts and
    // saved-for-later sessions still resolve to a real stack. The new
    // keys map 1:1 to the families loaded in the <link> in
    // sections/custom-print.liquid. Cursive families fall through to
    // `cursive`, serifs to `serif`, sans-serifs to `sans-serif`.
    const FONT_STACKS = {
        // Legacy keys from the previous Modern/Classic/Minimalist picker
        modern: "'Josefin Sans', sans-serif",
        classic: "Georgia, serif",
        minimalist: "'Helvetica Neue', Arial, sans-serif",
        // Regular
        'Josefin Sans': "'Josefin Sans', sans-serif",
        'Prata': "'Prata', serif",
        'Spectral': "'Spectral', serif",
        'Poppins': "'Poppins', sans-serif",
        'Playfair Display': "'Playfair Display', serif",
        'IM Fell English SC': "'IM Fell English SC', serif",
        // Handwritten
        'Dancing Script': "'Dancing Script', cursive",
        'Caveat': "'Caveat', cursive",
        'Kaushan Script': "'Kaushan Script', cursive",
        'Great Vibes': "'Great Vibes', cursive",
        'Sacramento': "'Sacramento', cursive",
        'Parisienne': "'Parisienne', cursive",
        'Alex Brush': "'Alex Brush', cursive",
        'Allura': "'Allura', cursive",
        'Yellowtail': "'Yellowtail', cursive",
        'Satisfy': "'Satisfy', cursive",
        'Pacifico': "'Pacifico', cursive",
        'Aguafina Script': "'Aguafina Script', cursive"
    };
    function resolveFontFamily(key) {
        return FONT_STACKS[key] || FONT_STACKS['Josefin Sans'];
    }

    // Filter definitions (CSS filter values)
    const FILTERS = {
        none: 'none',
        grayscale: 'grayscale(100%)',
        sepia: 'sepia(80%)',
        warm: 'sepia(30%) saturate(120%) hue-rotate(-10deg)',
        cool: 'saturate(90%) hue-rotate(15deg) brightness(105%)',
        vivid: 'saturate(150%) contrast(110%)'
    };

    // Size requirements for quality calculation (300 DPI)
    const SIZE_REQUIREMENTS = {
        'A4': { width: 2480, height: 3508, label: 'A4' },
        '30x40': { width: 3543, height: 4724, label: '30x40cm' },
        '40x50': { width: 4724, height: 5906, label: '40x50cm' },
        '50x70': { width: 5906, height: 8268, label: '50x70cm' },
        '60x80': { width: 7087, height: 9449, label: '60x80cm' }
    };

    // Size aspect ratios for preview (width/height for portrait orientation)
    const SIZE_RATIOS = {
        'A4': 2480 / 3508,     // 0.707
        '30x40': 3543 / 4724,  // 0.75
        '40x50': 4724 / 5906,  // 0.8
        '50x70': 5906 / 8268,  // 0.714
        '60x80': 7087 / 9449   // 0.75
    };

    // ============================================
    // State
    // ============================================
    let orderId = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);

    // Per-order upload token (trust-on-first-use). The backend binds the
    // first tokened upload to the order row and 403s later overwrites with a
    // different token, so a guessed order_id can't replace a draft's image.
    // Keyed by orderId in localStorage so draft-resume on this device reuses
    // the claimed token; a fresh device generates a new one (harmless on
    // unclaimed rows, rejected on claimed ones).
    function uploadTokenFor(id) {
        const key = 'cp_upload_token_' + id;
        let token = null;
        try { token = localStorage.getItem(key); } catch (e) { /* private mode */ }
        if (!token) {
            if (window.crypto && crypto.getRandomValues) {
                const buf = new Uint8Array(16);
                crypto.getRandomValues(buf);
                token = Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
            } else {
                token = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
            }
            try { localStorage.setItem(key, token); } catch (e) { /* private mode */ }
        }
        return token;
    }
    let uploadedImage = null;
    let originalImageUrl = null;
    let imageInfo = null;
    let isPlaceholderImage = false;
    const PLACEHOLDER_IMAGE_URL = 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/adventure-session-family-wedding-photography-photographer-photos-couple-session-portraits-IBphoto-oly-8158446.jpg?v=1772653803';
    let selectedSize = '30x40';
    let selectedFitMode = 'crop'; // Always crop to fill, no letterboxing
    let selectedType = '';
    let selectedFrame = '';
    let selectedFilter = 'none';
    let selectedOrientation = 'portrait';
    // Edit mode: the Framed selection came from the cart's frame add-on line
    // (not the unified Framed variant) — confirm must keep the two-line
    // structure. Set by the pairedFrame hydration in init().
    let cpFrameFromAddon = false;
    // Gift wrapping — 'none' | 'ribbon' | 'giftwrap' | 'bundle'. The gift
    // note + design + font live on the paired add-on's line-item
    // properties (read from the gift-note-card snippet's DOM at ATC time).
    let selectedGift = 'none';
    let selectedPaper = 'dots';
    // Hoisted to module scope so hydrateGiftFromCart (which runs after the
    // picker is bound) can re-sync the swatch UI once the paired add-on's
    // design is known.
    let cpPaperPicker = null;
    // Ribbon colour on the ribbon / bundle add-on (remembered per order id).
    let selectedRibbon = 'Red';
    let cpRibbonPicker = null;
    const CP_GIFT_IDS = {
        ribbon: 55259137802617,
        giftwrap: 56160831930745,
        bundle: 56160845988217
    };
    let cropSettings = {
        zoom: 100,
        rotation: 0,
        offsetX: 0,  // Stored as percentage of overflow (-100 to 100)
        offsetY: 0   // Stored as percentage of overflow (-100 to 100)
    };

    /**
     * Single source of truth for positioning an image inside a frame.
     * Used by both the crop modal preview and the main print preview, so
     * the modal preview is pixel-identical to what the print will render
     * (just at a different size).
     *
     * Sizing model: zoom 100 = image fully covers the frame (cover-fit).
     * Zoom > 100 zooms in further. offsetX / offsetY are percentages of
     * the available pan range (-100 = far left/top, 0 = centred, 100 =
     * far right/bottom). Rotation is 0/90/180/270.
     *
     * Implementation note: positioning uses width/height/left/top in CSS
     * pixels (no transform: translate). This was the failure mode in the
     * old design — modal and preview each did their own translate+scale
     * math against different frame sizes and the two drifted apart, plus
     * residual transforms corrupted measurements on modal re-open. Pure
     * geometry here means there's no state to leak between sessions.
     */
    function renderImage(frameEl, imgEl) {
        if (!frameEl || !imgEl) return;
        const fw = frameEl.clientWidth;
        const fh = frameEl.clientHeight;
        if (!fw || !fh) return;

        // Source dims: prefer the loaded <img>'s naturalWidth/Height — the
        // browser has ALREADY applied EXIF orientation, so this is the
        // authoritative *display* size. imageInfo from the upload endpoint
        // can carry RAW pre-EXIF dims (a portrait phone photo is often
        // stored as landscape pixels + a "rotate 90°" EXIF flag); trusting
        // it sizes the preview into the wrong-orientation box and stretches
        // the image. Fall back to imageInfo only when the <img> hasn't
        // loaded yet (naturalWidth still 0).
        let infoW = imgEl.naturalWidth;
        let infoH = imgEl.naturalHeight;
        if (!infoW || !infoH) {
            infoW = imageInfo && imageInfo.width;
            infoH = imageInfo && imageInfo.height;
        }
        if (!infoW || !infoH) return;

        const rot = ((cropSettings.rotation || 0) % 360 + 360) % 360;
        const isQuarter = (rot === 90 || rot === 270);

        // Source dims in their visual orientation (post-rotation).
        const srcW = isQuarter ? infoH : infoW;
        const srcH = isQuarter ? infoW : infoH;

        const cover = Math.max(fw / srcW, fh / srcH);
        const userZoom = Number.isFinite(cropSettings.zoom) ? cropSettings.zoom / 100 : 1;
        const scale = cover * Math.max(1, userZoom);

        // Visual rendered dims (the bounding box the user sees).
        const rw = srcW * scale;
        const rh = srcH * scale;

        // Pan range = how far the image overflows the frame on each side.
        const halfOverX = Math.max(0, (rw - fw) / 2);
        const halfOverY = Math.max(0, (rh - fh) / 2);
        const ox = Math.max(-1, Math.min(1, (cropSettings.offsetX || 0) / 100));
        const oy = Math.max(-1, Math.min(1, (cropSettings.offsetY || 0) / 100));

        // Visual top-left of the rotated image inside the frame.
        const visLeft = -halfOverX + halfOverX * ox;
        const visTop  = -halfOverY + halfOverY * oy;

        imgEl.style.position = 'absolute';
        imgEl.style.maxWidth = 'none';
        imgEl.style.maxHeight = 'none';
        imgEl.style.transformOrigin = 'center center';
        imgEl.style.transform = `rotate(${rot}deg)`;

        if (isQuarter) {
            // For 90°/270°, the <img>'s pre-rotation CSS box has
            // width = rh, height = rw. Solve for x,y so its rotated
            // bounding-box top-left lands at (visLeft, visTop).
            imgEl.style.width  = rh + 'px';
            imgEl.style.height = rw + 'px';
            imgEl.style.left = (visLeft + (rw - rh) / 2) + 'px';
            imgEl.style.top  = (visTop  + (rh - rw) / 2) + 'px';
        } else {
            imgEl.style.width  = rw + 'px';
            imgEl.style.height = rh + 'px';
            imgEl.style.left = visLeft + 'px';
            imgEl.style.top  = visTop  + 'px';
        }
    }

    // Personalization state
    let personalization = {
        headline: 'Your Title Here',
        caption: 'A meaningful date or place',
        font: 'Josefin Sans',
        border: 'white',
        textColor: 'auto'  // 'auto', 'white', or 'black' - used when border is 'none'
    };

    // Crop modal drag state
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragStartOffsetX = 0;
    let dragStartOffsetY = 0;

    // Cart tracking for edit mode (prevents duplicate items)
    let cartOrderIdPresent = [];
    let cartOrderIdKeys = [];
    let isEditMode = false;

    // ============================================
    // DOM Elements
    // ============================================
    const uploadZone = document.getElementById('upload-zone');
    const uploadPrompt = document.getElementById('upload-prompt');
    const fileInput = document.getElementById('image-upload');
    const previewImage = document.getElementById('preview-image');
    const previewContainer = document.getElementById('preview-container');
    const qualityBadge = document.getElementById('quality-badge');
    const qualityFill = document.getElementById('quality-fill');
    const qualityText = document.getElementById('quality-text');
    const editControls = document.getElementById('edit-controls');
    const filtersSection = document.getElementById('filters-section');
    const addToCartBtn = document.getElementById('add-to-cart-btn');
    const loadingOverlay = document.getElementById('loading-overlay');
    const priceDisplay = document.getElementById('price-display');

    // Crop modal elements
    const cropModal = document.getElementById('crop-modal');
    const cropPreviewImage = document.getElementById('crop-preview-image');
    const cropPreviewArea = document.getElementById('crop-preview-area');
    const zoomSlider = document.getElementById('zoom-slider');
    const rotateBtn = document.getElementById('rotate-btn');
    const cropConfirmBtn = document.getElementById('crop-confirm');
    const cropModalClose = document.getElementById('crop-modal-close');
    const cropUploadNew = document.getElementById('crop-upload-new');

    // Lightbox modal elements
    const lightboxModal = document.getElementById('lightbox-modal');
    const lightboxImage = document.getElementById('lightbox-image');
    const lightboxFrame = document.getElementById('lightbox-frame');
    const lightboxOverlay = document.getElementById('lightbox-overlay');
    const lightboxHeadline = document.getElementById('lightbox-headline');
    const lightboxCaption = document.getElementById('lightbox-caption');
    const lightboxClose = document.getElementById('lightbox-close');

    // ============================================
    // Initialization
    // ============================================
    async function init() {
        console.log('[Custom Print] Initializing...');
        console.log('[Custom Print] Current URL:', window.location.href);
        console.log('[Custom Print] Search params:', window.location.search);

        setupHeaderHeight();
        setupUploadZone();
        setupProductPickers();
        setupAddToCart();
        setupFilters();
        setupCropModal();
        setupEditControls();
        setupPersonalization();
        setupLightbox();
        setupStepTabs();

        // Force the default selection to Print + A4 on first load (rather
        // than whatever forloop.first happens to render in the Liquid),
        // which is the most popular configuration. Falls back gracefully
        // if those tiles aren't present (e.g. product variants change).
        const printTile = document.querySelector('#product-type-picker .elem__picker[data-option="Print"]');
        if (printTile) {
            document.querySelectorAll('#product-type-picker .elem__picker').forEach(e => e.classList.remove('active__picker'));
            printTile.classList.add('active__picker');
        }
        const a4Tile = document.querySelector('#size-picker .elem__picker[data-option="A4"]');
        if (a4Tile) {
            document.querySelectorAll('#size-picker .elem__picker').forEach(e => e.classList.remove('active__picker'));
            a4Tile.classList.add('active__picker');
        }

        // Initialize selected options from (now-overridden) active elements
        const firstType = document.querySelector('#product-type-picker .active__picker');
        if (firstType) selectedType = firstType.dataset.option;

        // Update size picker prices for initial product type
        updateSizePickerPrices();

        const firstSize = document.querySelector('#size-picker .active__picker');
        if (firstSize) {
            selectedSize = firstSize.dataset.sizeCode || 'A4';
            updatePrice(firstSize.dataset.price);
            // Initialize aspect ratio for default size
            updatePreviewRatio(selectedSize);
        }

        const firstFrame = document.querySelector('#frame-picker .active__picker');
        if (firstFrame) selectedFrame = firstFrame.dataset.option;

        // Apply initial frame visibility based on selected type
        updateFrameVisibility();
        // Apply initial frame-colour availability based on size selection
        // (so e.g. Dark Brown is greyed out from page-load when 30×40 is
        // the default size).
        updateFramePickerAvailability();
        // Sync #var_id to the forced Print+A4 default. The Liquid-rendered
        // value is the product's FIRST variant (Framed/A4/Black) — without
        // this, a customer who never clicks a picker gets carted at the
        // framed price while the page displays the Print price.
        updateVariantId();

        // Load cart items first (to track existing orders)
        await loadCartItems();

        // Check for order_id in URL (Edit Personalisations from cart)
        const urlParams = new URLSearchParams(window.location.search);
        const existingOrderId = urlParams.get('order_id');

        console.log('[Custom Print] URL order_id parameter:', existingOrderId);

        if (existingOrderId) {
            console.log('[Custom Print] Loading existing order:', existingOrderId);
            isEditMode = true;
            orderId = existingOrderId; // Set orderId early for cart tracking

            // setupProductPickers() ran earlier against the throwaway random
            // orderId assigned at module load, so any recallPaper() lookup
            // it made was keyed to the wrong id. Re-derive against the real
            // order id now and re-sync the swatch UI (hydrateGiftFromCart
            // below will further correct this from the paired cart line
            // when one exists, which is authoritative over localStorage).
            if (window.SkylitGifts) {
                selectedPaper = window.SkylitGifts.recallPaperFor('cp_' + orderId) || selectedPaper;
                if (cpPaperPicker) cpPaperPicker.set(selectedPaper);
                selectedRibbon = window.SkylitGifts.recallRibbonFor('cp_' + orderId) || selectedRibbon;
                if (cpRibbonPicker) cpRibbonPicker.set(selectedRibbon);
                updateGiftUI();
            }

            // Show edit mode indicator
            showEditModeIndicator();

            // Log the revisit so admin can see customers who returned to
            // verify their design — even if they leave without changes
            // (autosave would produce no diff in that case).
            try {
                fetch(API_BASE + 'api/order-event', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        order_id: existingOrderId,
                        event: 'edit_personalisations_opened',
                        detail: window.location.href.slice(0, 200)
                    })
                }).catch(function() {});
            } catch (e) {}

            await loadExistingOrder(existingOrderId);

            // A frame bought from the cart's "Complete the look" upsell:
            // show it as the Framed selection so the customer sees the frame
            // they own. submitToCart() keeps the two-line structure — the
            // print re-adds as Print and the add-on line is synced. Driven
            // through real tile clicks so preview/price/#var_id all follow.
            if (isEditMode && window.SkylitPairing && window.SkylitPairing.pairedFrame) {
                try {
                    const pairedFrame = await window.SkylitPairing.pairedFrame(orderId);
                    if (pairedFrame && selectedType === 'Print') {
                        const typeTile = document.querySelector('#product-type-picker .elem__picker[data-option="Framed"]');
                        if (typeTile) {
                            typeTile.click();
                            const frameTile = document.querySelector('#frame-picker .elem__picker[data-option="' + pairedFrame.colour + '"]');
                            if (frameTile && !frameTile.classList.contains('sold-out')) frameTile.click();
                            cpFrameFromAddon = true;
                        }
                    }
                } catch (e) { /* frame adoption is best-effort */ }
            }
        } else {
            console.log('[Custom Print] Initialized with new order ID:', orderId);
            // Load placeholder image so users can explore the UI immediately
            await loadPlaceholderImage();
        }
    }

    // Show visual indicator that we're in edit mode
    function showEditModeIndicator() {
        const titleEl = document.querySelector('.custom-print-title');
        if (titleEl && !document.querySelector('.edit-mode-badge')) {
            const badge = document.createElement('span');
            badge.className = 'edit-mode-badge';
            badge.style.cssText = 'display: inline-block; background: #633e29; color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 10px; margin-left: 8px; vertical-align: middle;';
            badge.textContent = 'EDITING';
            titleEl.appendChild(badge);
        }
    }

    // Load placeholder image so users see the UI populated on first visit
    async function loadPlaceholderImage() {
        try {
            isPlaceholderImage = true;
            await loadImageFromUrl(PLACEHOLDER_IMAGE_URL);
            // Override add-to-cart to stay disabled with placeholder
            updateAddToCartButton();
            toggleSampleNudge(true);
            console.log('[Custom Print] Placeholder image loaded');
        } catch (e) {
            console.warn('[Custom Print] Could not load placeholder image:', e);
            isPlaceholderImage = false;
        }
    }

    // Show/hide the "tap to add your photo" nudge that overlays the sample
    // image. Only ever visible while the placeholder image is on screen.
    function toggleSampleNudge(show) {
        const nudge = document.getElementById('cp-sample-nudge');
        if (nudge) nudge.classList.toggle('show', show);
        const replaceBtn = document.getElementById('replace-btn');
        if (replaceBtn) replaceBtn.classList.toggle('cp-nudge-pulse', show);
    }

    // Show prompt when image needs to be re-uploaded (settings were restored but image wasn't available)
    function showImageReuploadPrompt() {
        const uploadPromptEl = document.getElementById('upload-prompt');
        if (uploadPromptEl) {
            // Update the upload prompt text to indicate this is a re-upload
            const textEl = uploadPromptEl.querySelector('.upload-prompt-text');
            const subtextEl = uploadPromptEl.querySelector('.upload-prompt-subtext');

            if (textEl) {
                textEl.innerHTML = '📷 <strong>Re-upload your image</strong>';
            }
            if (subtextEl) {
                subtextEl.innerHTML = 'Your settings were restored, but please upload your image again<br><small style="color: rgba(244, 228, 186, 0.6);">JPG, PNG or TIFF - Max 50MB</small>';
            }

            // Add a highlight effect
            uploadPromptEl.style.borderColor = 'rgba(255, 181, 3, 0.5)';
            uploadPromptEl.style.borderWidth = '3px';
        }
    }

    // ============================================
    // Load Cart Items (for edit mode tracking)
    // ============================================
    async function loadCartItems() {
        try {
            const response = await fetch('/cart.js');
            const cart = await response.json();

            // Clear arrays first to avoid duplicates on page reload
            cartOrderIdPresent = [];
            cartOrderIdKeys = [];

            cart.items.forEach((item) => {
                if (item.properties) {
                    // Support both new (_OrderID) and legacy (OrderID) formats
                    const itemOrderId = item.properties._OrderID || item.properties.OrderID;
                    if (itemOrderId) {
                        // Skip enhancement services - only track print items for edit mode
                        const title = (item.product_title || item.title || '').toLowerCase();
                        const isEnhancement = title.includes('enhancement') || title.includes('upscale');
                        if (!isEnhancement) {
                            cartOrderIdPresent.push(itemOrderId);
                            cartOrderIdKeys.push(item.key);
                            console.log('[Custom Print] Found print item with OrderID:', itemOrderId, 'key:', item.key);
                        } else {
                            console.log('[Custom Print] Skipping enhancement service with OrderID:', itemOrderId);
                        }
                    }
                }
            });

            console.log('[Custom Print] Cart print order IDs loaded:', cartOrderIdPresent);
        } catch (error) {
            console.error('[Custom Print] Failed to load cart items:', error);
        }
    }

    // Add-to-cart navigates to /cart, so pressing Back restores this page from
    // the back-forward cache with the pre-add state still in memory. That state
    // is stale in a way specific to this customizer: submitToCart mints a FRESH
    // orderId immediately after a successful add ("prevents accidental edits"),
    // but never clears isEditMode. So a customer who arrived via the cart's Edit
    // link, confirmed, then pressed Back was looking at a page whose orderId is
    // no longer in the cart while the CTA still read "Confirm Edits" and the
    // EDITING badge was still up. Pressing it ran purgeDesign() against an
    // orderId the cart has never seen — purging nothing — and ADDED a second
    // print, having promised to update the first.
    //
    // Re-derive edit mode from the live cart against the CURRENT orderId so the
    // label matches what the button will actually do. Mirrors the pageshow
    // handlers in locationmap.js, hor-moon-myjs.js and name-landsat.js. Note
    // custom print has no autosave (submitToCart is its only write), so unlike
    // those it has never been able to silently overwrite a confirmed design —
    // this is purely about the CTA telling the truth.
    window.addEventListener('pageshow', async function(event) {
        if (!event.persisted) return;
        await loadCartItems();
        isEditMode = cartOrderIdPresent.indexOf(orderId) !== -1;
        if (!isEditMode) {
            const badge = document.querySelector('.edit-mode-badge');
            if (badge) badge.remove();
        } else {
            showEditModeIndicator();
        }
        updateAddToCartButton();
        console.log('[Custom Print] bfcache restore — cart re-read, editMode=' + isEditMode);
    });

    // ============================================
    // Load Existing Order (Edit from Cart)
    // ============================================
    async function loadExistingOrder(existingId) {
        showLoading('Loading your design...');

        try {
            // Use the existing order ID
            orderId = existingId;
            console.log('[Custom Print] Loading existing order:', orderId);

            // Fetch order data from backend (uses order_no param)
            const response = await fetch(API_BASE + 'api/custom-print-data/?order_no=' + existingId);

            if (!response.ok) {
                throw new Error('Order not found - status: ' + response.status);
            }

            const data = await response.json();
            console.log('[Custom Print] Loaded order data:', data);

            // Refuse to hydrate a paid order — its render is already produced
            // and any further edit would corrupt the shipped artwork. DUP-
            // prefixed duplicates are exempt and stay editable.
            // (shopify_order_id is stripped by sanitizeForPublic, so we rely
            // on funnel_status which the webhook flips to 'paid'.)
            const isDup = typeof existingId === 'string' && existingId.indexOf('DUP-') === 0;
            const isPaid = data && data.funnel_status === 'paid';
            if (isPaid && !isDup) {
                console.log('[Custom Print] paid order revisited, starting fresh', existingId);
                const p = new URLSearchParams(window.location.search);
                p.delete('order_id');
                const qs = p.toString();
                window.location.replace(window.location.pathname + (qs ? '?' + qs : ''));
                return;
            }

            // Restore settings from saved data
            // Handle both flattened format (from backend) and nested format (as sent)
            const filterValue = data.filter || data._filter;
            if (filterValue) {
                selectedFilter = filterValue;
                // Update filter UI
                document.querySelectorAll('.filter-option').forEach(opt => {
                    opt.classList.toggle('active', opt.dataset.filter === selectedFilter);
                });
            }

            const orientationValue = data.orientation || data._orientation;
            if (orientationValue) {
                selectedOrientation = orientationValue;
                // Update orientation UI
                document.querySelectorAll('.orientation-btn').forEach(btn => {
                    btn.classList.toggle('active', btn.dataset.orientation === selectedOrientation);
                });
                // Update preview orientation
                uploadZone.classList.toggle('landscape', selectedOrientation === 'landscape');
                if (cropPreviewArea) {
                    cropPreviewArea.classList.toggle('landscape', selectedOrientation === 'landscape');
                    cropPreviewArea.classList.toggle('portrait', selectedOrientation === 'portrait');
                }
            }

            // Restore crop settings - handle both nested and flattened formats
            // Nested format: data.crop = {zoom, rotation, offsetX, offsetY}
            // Flattened format: data.crop_zoom, data.crop_rotation, data.crop_offset_x, data.crop_offset_y
            if (data.crop && typeof data.crop === 'object') {
                // Nested format
                cropSettings = {
                    zoom: data.crop.zoom || 100,
                    rotation: data.crop.rotation || 0,
                    offsetX: data.crop.offsetX || data.crop.offset_x || 0,
                    offsetY: data.crop.offsetY || data.crop.offset_y || 0
                };
            } else {
                // Flattened format
                cropSettings = {
                    zoom: data.crop_zoom || 100,
                    rotation: data.crop_rotation || 0,
                    offsetX: data.crop_offset_x || 0,
                    offsetY: data.crop_offset_y || 0
                };
            }
            if (zoomSlider) zoomSlider.value = cropSettings.zoom;
            console.log('[Custom Print] Restored crop settings:', cropSettings);

            // Restore personalization fields - handle both nested and flattened formats
            // Nested format: data.personalization = {headline, caption, font, border, textColor}
            // Flattened format: data.headline, data.caption, data.font, data.text_border, data.text_color
            if (data.personalization && typeof data.personalization === 'object') {
                // Nested format
                personalization = {
                    headline: data.personalization.headline || '',
                    caption: data.personalization.caption || '',
                    font: data.personalization.font || 'modern',
                    border: data.personalization.border || 'white',
                    textColor: data.personalization.textColor || data.personalization.text_color || 'auto'
                };
            } else {
                // Flattened format
                personalization = {
                    headline: data.headline || '',
                    caption: data.caption || '',
                    font: data.font || 'modern',
                    border: data.text_border || data.border || 'white',
                    textColor: data.text_color || 'auto'
                };
            }
            console.log('[Custom Print] Restored personalization:', personalization);

            // Update personalization UI
            const headlineInput = document.getElementById('headline-input');
            const captionInput = document.getElementById('caption-input');
            if (headlineInput) headlineInput.value = personalization.headline;
            if (captionInput) captionInput.value = personalization.caption;

            // Update font picker
            document.querySelectorAll('#font-picker .option-btn').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.font === personalization.font);
            });

            // Update border picker
            document.querySelectorAll('#border-picker .option-btn').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.border === personalization.border);
            });

            // Update text color picker
            document.querySelectorAll('#text-color-picker .option-btn').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.textcolor === personalization.textColor);
            });

            // Show/hide text color field based on border selection
            updateTextColorFieldVisibility();

            // Restore product type selection - handle both formats
            const typeValue = data.selected_type || data._selected_type;
            if (typeValue) {
                selectedType = typeValue;
                document.querySelectorAll('#product-type-picker .elem__picker').forEach(el => {
                    el.classList.toggle('active__picker', el.dataset.option === selectedType);
                });
                updateFrameVisibility();
            }

            // Restore size selection
            const sizeValue = data.size || data._selected_size;
            if (sizeValue) {
                // Remove 'cm' suffix if present
                selectedSize = sizeValue.replace('cm', '');
                document.querySelectorAll('#size-picker .elem__picker').forEach(el => {
                    if (el.dataset.sizeCode === selectedSize) {
                        el.classList.add('active__picker');
                        updatePrice(el.dataset.price);
                    } else {
                        el.classList.remove('active__picker');
                    }
                });
                // Update aspect ratio for restored size
                updatePreviewRatio(selectedSize);
            }

            // Restore frame selection - handle both formats
            const frameValue = data.selected_frame || data._selected_frame;
            if (frameValue) {
                selectedFrame = frameValue;
                document.querySelectorAll('#frame-picker .elem__picker').forEach(el => {
                    el.classList.toggle('active__picker', el.dataset.option === selectedFrame);
                });
            }

            // Sync #var_id to the restored type/size/frame. The picker click
            // handlers normally do this, but hydration sets the selections
            // directly — without this the submit carts the page-load default
            // variant instead of the order's actual configuration.
            updateVariantId();

            // Restore gift wrapping from the paired add-on already in cart
            // (gift state lives on the add-on line item, not the print row).
            await hydrateGiftFromCart();

            // Load the image - prioritize original quality
            // Try multiple sources in order of quality (highest first)
            let imageUrl = data.original_image || data.image_url || data.image;
            console.log('[Custom Print] Image URL check:', {
                original_image: data.original_image,
                thumb_file: data.thumb_file,
                image_url: data.image_url,
                resolved: imageUrl
            });

            // If it's a relative path, prepend the API base URL
            if (imageUrl && !imageUrl.startsWith('http')) {
                const baseUrl = API_BASE.replace(/\/$/, ''); // Remove trailing slash
                imageUrl = baseUrl + (imageUrl.startsWith('/') ? '' : '/') + imageUrl;
                console.log('[Custom Print] Converted to absolute URL:', imageUrl);
            }

            // Define fallback endpoints in order of quality (original first, then thumbnail)
            const originalEndpoint = API_BASE + 'api/custom-print-original/?orderId=' + existingId;
            const thumbnailEndpoint = API_BASE + 'api/custom-print-thumbnail/?orderId=' + existingId;

            // Try to load image with fallback chain: media URL -> original endpoint -> thumbnail endpoint
            let imageLoaded = false;

            // First: Try the media URL from serializer (if available)
            if (imageUrl) {
                console.log('[Custom Print] Trying media URL:', imageUrl);
                try {
                    await loadImageFromUrl(imageUrl);
                    imageLoaded = true;
                } catch (imgError) {
                    console.warn('[Custom Print] Media URL failed (likely CORS):', imgError.message);
                }
            }

            // Second: Try the original image endpoint (full quality, with proper CORS headers)
            if (!imageLoaded) {
                console.log('[Custom Print] Trying original image endpoint:', originalEndpoint);
                try {
                    await loadImageFromUrl(originalEndpoint);
                    imageLoaded = true;
                } catch (origError) {
                    console.warn('[Custom Print] Original endpoint failed:', origError.message);
                }
            }

            // Third: Fall back to thumbnail endpoint (lower quality, but better than nothing)
            if (!imageLoaded) {
                console.log('[Custom Print] Trying thumbnail endpoint as last resort:', thumbnailEndpoint);
                try {
                    await loadImageFromUrl(thumbnailEndpoint);
                    imageLoaded = true;
                    console.warn('[Custom Print] Loaded thumbnail - original image not available');
                } catch (thumbError) {
                    console.error('[Custom Print] All image sources failed');
                }
            }

            // If all attempts failed, show re-upload prompt
            if (!imageLoaded) {
                console.error('[Custom Print] Could not load image from any source');
                showImageReuploadPrompt();
            }

            hideLoading();
            console.log('[Custom Print] Order loaded successfully');

            // Check if enhancement service is in cart for this order
            await checkEnhancementInCart(existingId);

        } catch (error) {
            console.error('[Custom Print] Failed to load order:', error);
            hideLoading();

            // Show error indicator on page
            const titleEl = document.querySelector('.custom-print-title');
            if (titleEl) {
                const errorBadge = document.createElement('div');
                errorBadge.style.cssText = 'background: #fee2e2; color: #dc2626; padding: 8px 12px; border-radius: 6px; font-size: 12px; margin-top: 8px; text-align: center;';
                errorBadge.innerHTML = '<strong>Edit failed:</strong> ' + error.message + '<br><small>Please re-upload your image</small>';
                titleEl.parentNode.insertBefore(errorBadge, titleEl.nextSibling);
            }

            // Generate new order ID if loading failed
            orderId = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
            console.log('[Custom Print] Falling back to new order ID:', orderId);

            // Show user-friendly message
            alert('Could not load your previous design (Error: ' + error.message + '). You may need to re-upload your image.');
        }
    }

    async function loadImageFromUrl(imageUrl) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = function() {
                // Store image info
                imageInfo = {
                    width: img.naturalWidth,
                    height: img.naturalHeight
                };
                originalImageUrl = imageUrl;
                uploadedImage = imageUrl;

                // Update preview - set crossOrigin for canvas operations
                previewImage.crossOrigin = 'anonymous';
                previewImage.src = imageUrl;
                previewImage.classList.add('show');

                // Determine if image is landscape
                const isLandscapeImg = img.naturalWidth > img.naturalHeight;
                previewImage.classList.toggle('landscape-img', isLandscapeImg);
                previewImage.classList.toggle('portrait-img', !isLandscapeImg);

                // Apply saved filter
                previewImage.style.filter = FILTERS[selectedFilter] || 'none';

                // Hide upload prompt, show controls
                uploadPrompt.classList.add('hidden');
                uploadZone.classList.add('has-image');
                editControls.classList.add('show');
                filtersSection.classList.add('show');
                qualityBadge.classList.add('show');

                // Ensure orientation class is applied (for edit mode restoration)
                uploadZone.classList.toggle('landscape', selectedOrientation === 'landscape');
                console.log('[Custom Print] Applied orientation:', selectedOrientation, 'cropSettings:', cropSettings);

                // Reset upscale state for new image
                upscaleRequested = false;
                resetUpscaleButton();

                // Update quality badge
                revalidateQuality();

                // Generate filter thumbnails
                generateFilterThumbnails(imageUrl);

                // Apply saved crop settings to preview after a brief delay
                // to ensure CSS has been applied and dimensions are available
                requestAnimationFrame(() => {
                    applyPreviewTransforms();
                });

                // Update personalization overlay
                updatePersonalizationPreview();

                // Enable add to cart
                updateAddToCartButton();

                console.log('[Custom Print] Image loaded from URL:', imageUrl);
                resolve();
            };

            img.onerror = function() {
                console.error('[Custom Print] Failed to load image from URL');
                reject(new Error('Failed to load image'));
            };

            img.src = imageUrl;
        });
    }

    function applyPreviewTransform() {
        // Apply rotation and zoom to preview image based on cropSettings
        if (!previewImage) return;

        const rotation = cropSettings.rotation || 0;
        const zoom = (cropSettings.zoom || 100) / 100;

        // Calculate offset based on crop settings
        const offsetX = cropSettings.offsetX || 0;
        const offsetY = cropSettings.offsetY || 0;

        // Apply transform - preview uses simpler positioning
        previewImage.style.transform = `translate(-50%, -50%) rotate(${rotation}deg) scale(${zoom}) translate(${offsetX}%, ${offsetY}%)`;
    }

    // ============================================
    // Header Height Detection
    // ============================================
    function setupHeaderHeight() {
        function updateHeaderHeight() {
            let totalHeight = 0;

            // Method 1: Try to get the header group (includes announcement bar + header)
            const headerGroup = document.querySelector('.shopify-section-group-header-group');
            if (headerGroup) {
                totalHeight = headerGroup.getBoundingClientRect().height;
            }

            // Method 2: If no header group, calculate from individual sections
            if (totalHeight === 0) {
                // Get announcement bar height (multiple possible selectors)
                const announcementSelectors = [
                    '.shopify-section-announcement-bar',
                    '[class*="announcement"]',
                    '.announcement-bar-section',
                    '#shopify-section-announcement-bar'
                ];
                for (const selector of announcementSelectors) {
                    const el = document.querySelector(selector);
                    if (el) {
                        totalHeight += el.getBoundingClientRect().height;
                        break;
                    }
                }

                // Get header height
                const headerSelectors = [
                    '.shopify-section-header',
                    '#shopify-section-header',
                    '.header-wrapper',
                    'header.header'
                ];
                for (const selector of headerSelectors) {
                    const el = document.querySelector(selector);
                    if (el) {
                        totalHeight += el.getBoundingClientRect().height;
                        break;
                    }
                }
            }

            // Method 3: Fallback - measure distance from top of custom-print-app to top of viewport
            if (totalHeight === 0) {
                const app = document.querySelector('.custom-print-app');
                if (app) {
                    const appRect = app.getBoundingClientRect();
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    totalHeight = appRect.top + scrollTop;
                }
            }

            // Set the CSS variable with a minimum fallback of 120px
            const app = document.querySelector('.custom-print-app');
            if (app) {
                const finalHeight = Math.max(totalHeight, 120);
                app.style.setProperty('--header-height', finalHeight + 'px');
                console.log('[Custom Print] Header height set to:', finalHeight + 'px');
            }
        }

        // Initial update
        updateHeaderHeight();

        // Initial update after a short delay to ensure DOM is ready
        updateHeaderHeight();
        setTimeout(updateHeaderHeight, 100);
        setTimeout(updateHeaderHeight, 500);

        // Update on resize (header might change size)
        window.addEventListener('resize', updateHeaderHeight);

        // Observe header group for changes (sticky headers, etc.)
        const headerGroup = document.querySelector('.shopify-section-group-header-group') ||
                           document.querySelector('.shopify-section-header');
        if (headerGroup) {
            const resizeObserver = new ResizeObserver(updateHeaderHeight);
            resizeObserver.observe(headerGroup);
        }
    }

    // ============================================
    // Personalization
    // ============================================
    function setupPersonalization() {
        // Toggle section
        const toggleBtn = document.getElementById('personalization-toggle');
        const section = document.getElementById('personalization-section');
        if (toggleBtn && section) {
            toggleBtn.addEventListener('click', () => {
                section.classList.toggle('open');
            });
        }

        // Headline input
        const headlineInput = document.getElementById('headline-input');
        const headlineClear = document.getElementById('headline-clear');
        if (headlineInput) {
            headlineInput.addEventListener('input', (e) => {
                personalization.headline = e.target.value;
                updatePersonalizationPreview();
            });
        }
        if (headlineClear && headlineInput) {
            headlineClear.addEventListener('click', () => {
                headlineInput.value = '';
                personalization.headline = '';
                updatePersonalizationPreview();
            });
        }

        // Caption input
        const captionInput = document.getElementById('caption-input');
        const captionClear = document.getElementById('caption-clear');
        if (captionInput) {
            captionInput.addEventListener('input', (e) => {
                personalization.caption = e.target.value;
                updatePersonalizationPreview();
            });
        }
        if (captionClear && captionInput) {
            captionClear.addEventListener('click', () => {
                captionInput.value = '';
                personalization.caption = '';
                updatePersonalizationPreview();
            });
        }

        // Font picker
        const fontBtns = document.querySelectorAll('#font-picker .option-btn');
        fontBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                fontBtns.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                personalization.font = btn.dataset.font;
                updatePersonalizationPreview();
            });
        });

        // Border picker
        const borderBtns = document.querySelectorAll('#border-picker .option-btn');
        borderBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                borderBtns.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                personalization.border = btn.dataset.border;
                updateTextColorFieldVisibility();
                updatePersonalizationPreview();

                // Recalculate auto-zoom after border change (frame dimensions change)
                if (imageInfo && uploadedImage) {
                    setTimeout(() => {
                        const autoZoom = calculateAutoZoomForOrientation();
                        const minZoom = Math.max(100, autoZoom);

                        // Update zoom if below minimum
                        if (cropSettings.zoom < minZoom) {
                            cropSettings.zoom = minZoom;
                            if (zoomSlider) {
                                zoomSlider.min = minZoom;
                                zoomSlider.value = minZoom;
                            }
                        }

                        applyPreviewTransforms();
                    }, 50);
                }
            });
        });

        // Text color picker (shown when border is 'none')
        const textColorBtns = document.querySelectorAll('#text-color-picker .option-btn');
        textColorBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                textColorBtns.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                personalization.textColor = btn.dataset.textcolor;
                updatePersonalizationPreview();
            });
        });

        // Create personalization overlay elements
        createPersonalizationOverlay();
    }

    // Show/hide text color field based on border selection
    function updateTextColorFieldVisibility() {
        const textColorField = document.getElementById('text-color-field');
        if (textColorField) {
            const showField = personalization.border === 'none';
            textColorField.style.display = showField ? 'block' : 'none';
        }
    }

    // Detect image brightness for auto text color selection
    // Returns 'white' for dark images, 'black' for light images
    function detectImageBrightness(imageElement) {
        if (!imageElement || !imageElement.complete || imageElement.naturalWidth === 0) {
            return 'white'; // Default to white text for safety
        }

        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Sample at a smaller size for performance
            const sampleSize = 50;
            canvas.width = sampleSize;
            canvas.height = sampleSize;

            // Draw the image scaled down
            ctx.drawImage(imageElement, 0, 0, sampleSize, sampleSize);

            // Get image data - focus on bottom area where text appears
            const bottomY = Math.floor(sampleSize * 0.7); // Bottom 30% of image
            const imageData = ctx.getImageData(0, bottomY, sampleSize, sampleSize - bottomY);
            const data = imageData.data;

            // Calculate average brightness
            let totalBrightness = 0;
            const pixelCount = data.length / 4;

            for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                // Perceived brightness formula
                totalBrightness += (r * 0.299 + g * 0.587 + b * 0.114);
            }

            const avgBrightness = totalBrightness / pixelCount;
            console.log('[Custom Print] Image brightness detected:', avgBrightness);

            // If average brightness is below 128 (mid-gray), use white text
            return avgBrightness < 128 ? 'white' : 'black';
        } catch (e) {
            console.warn('[Custom Print] Could not detect image brightness:', e);
            return 'white'; // Default to white on error
        }
    }

    function createPersonalizationOverlay() {
        // Create text overlay container for preview
        const previewInner = document.getElementById('upload-zone');
        if (!previewInner) return;

        // Check if overlay already exists
        if (document.getElementById('personalization-overlay')) return;

        const overlay = document.createElement('div');
        overlay.id = 'personalization-overlay';
        overlay.className = 'personalization-overlay';
        overlay.innerHTML = `
            <div class="personalization-text-area">
                <div class="personalization-headline" id="preview-headline"></div>
                <div class="personalization-caption" id="preview-caption"></div>
            </div>
        `;
        previewInner.appendChild(overlay);
    }

    function updatePersonalizationPreview() {
        const headlineEl = document.getElementById('preview-headline');
        const captionEl = document.getElementById('preview-caption');
        const overlay = document.getElementById('personalization-overlay');
        const previewInner = document.getElementById('upload-zone');

        if (!headlineEl || !captionEl || !overlay || !previewInner) return;

        // Update text content
        headlineEl.textContent = personalization.headline;
        captionEl.textContent = personalization.caption;

        // Apply font style
        const fontFamily = resolveFontFamily(personalization.font);
        headlineEl.style.fontFamily = fontFamily;
        captionEl.style.fontFamily = fontFamily;

        // Apply border color to preview container
        const borderColors = {
            white: '#ffffff',
            beige: '#f5f0e6',
            black: '#0a0a0a',
            'dark-brown': '#1a1310',
            none: 'transparent'
        };
        const borderColor = borderColors[personalization.border] || borderColors.white;

        // Check if we have text or just border
        const hasText = personalization.headline || personalization.caption;
        const hasBorder = personalization.border !== 'none';

        // Update preview container border. The matte pseudo-element and
        // the .preview-inner background both pull from --matte-color so
        // the bottom strip recolours along with the border (without this,
        // changing border to Black left the bottom matte stuck on white).
        previewInner.style.borderColor = borderColor;
        previewInner.style.setProperty('--matte-color', borderColor);
        previewInner.classList.toggle('has-border', hasBorder);
        // Mirror the same colour onto the crop modal preview so what the
        // user sees there matches the print preview exactly.
        if (cropPreviewArea) {
            cropPreviewArea.style.borderColor = borderColor;
            cropPreviewArea.style.setProperty('--matte-color', borderColor);
            cropPreviewArea.classList.toggle('has-border', hasBorder);
        }

        // Add border-only class when border but no text (equal spacing all sides)
        previewInner.classList.toggle('border-only', hasBorder && !hasText);
        if (cropPreviewArea) cropPreviewArea.classList.toggle('border-only', hasBorder && !hasText);

        // Also update the outer preview-container background to match mat color
        const previewContainerEl = document.getElementById('preview-container');
        if (previewContainerEl) {
            previewContainerEl.style.backgroundColor = hasBorder ? borderColor : '#1a1a2e';
        }

        // Determine text color based on border and user preference
        let textColor;
        if (personalization.border === 'none') {
            // No border - use text color selection (auto-detect or manual)
            if (personalization.textColor === 'auto') {
                // Auto-detect from image
                const imgEl = document.getElementById('preview-image');
                const detectedColor = detectImageBrightness(imgEl);
                textColor = detectedColor === 'white' ? '#ffffff' : '#0a0a0a';
            } else {
                textColor = personalization.textColor === 'white' ? '#ffffff' : '#0a0a0a';
            }
            // Add text shadow for better readability on image
            const shadowColor = textColor === '#ffffff' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)';
            headlineEl.style.textShadow = `0 1px 3px ${shadowColor}`;
            captionEl.style.textShadow = `0 1px 3px ${shadowColor}`;
        } else {
            // Has border - use contrast based on border color
            textColor = (personalization.border === 'black' || personalization.border === 'dark-brown') ? '#ffffff' : '#0a0a0a';
            headlineEl.style.textShadow = 'none';
            captionEl.style.textShadow = 'none';
        }
        headlineEl.style.color = textColor;
        captionEl.style.color = textColor;

        // Show/hide overlay - only show if there's text
        overlay.classList.toggle('show', hasText);
        overlay.style.backgroundColor = borderColor;
    }

    // ============================================
    // Upload Zone
    // ============================================
    function setupUploadZone() {
        // Click on upload prompt to upload
        if (uploadPrompt) {
            uploadPrompt.addEventListener('click', (e) => {
                e.stopPropagation();
                fileInput.click();
            });
        }

        // File input change
        fileInput.addEventListener('change', (e) => {
            if (e.target.files && e.target.files[0]) {
                handleFile(e.target.files[0]);
            }
        });

        // Drag and drop on upload zone
        uploadZone.addEventListener('dragover', (e) => {
            e.preventDefault();
            uploadZone.classList.add('drag-over');
        });

        uploadZone.addEventListener('dragleave', () => {
            uploadZone.classList.remove('drag-over');
        });

        uploadZone.addEventListener('drop', (e) => {
            e.preventDefault();
            uploadZone.classList.remove('drag-over');
            if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                handleFile(e.dataTransfer.files[0]);
            }
        });
    }

    // ============================================
    // File Handling
    // ============================================
    async function handleFile(file) {
        // Clear placeholder flag — user is uploading their own image
        isPlaceholderImage = false;
        toggleSampleNudge(false);

        // Validate file type
        if (!ALLOWED_TYPES.includes(file.type)) {
            showError('Please upload a JPG, PNG, or TIFF image.');
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
            showError('File is too large. Maximum size is 50MB.');
            return;
        }

        // Show loading
        showLoading('Uploading your image...');

        try {
            // Create FormData
            const formData = new FormData();
            formData.append('image', file);
            formData.append('order_id', orderId);
            formData.append('upload_token', uploadTokenFor(orderId));
            formData.append('size', selectedSize);
            formData.append('fit_mode', selectedFitMode);

            // Upload to backend
            const response = await fetch(API_BASE + 'api/custom-print-upload/', {
                method: 'POST',
                body: formData
            });
            // Dual-write: also upload to admin app (R2 storage)
            fetch(ADMIN_BASE + 'api/custom-print-upload/', {
                method: 'POST',
                body: formData
            }).catch(() => {});

            const data = await response.json();

            if (data.success) {
                // Use original/full image for preview, thumbnail for cart
                // Add cache-busting to force reload on new upload
                const baseUrl = data.image_url || data.thumbnail_url;
                const cacheBuster = (baseUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
                originalImageUrl = baseUrl + cacheBuster;
                // Prefer thumbnail_url for cart line-item icon; fall back to
                // image_url if the upload endpoint didn't return a thumbnail.
                // Without this fallback, a missing thumbnail_url leaves
                // uploadedImage = undefined and the ATC button stays disabled
                // even though the preview rendered fine.
                uploadedImage = data.thumbnail_url || data.image_url || baseUrl;
                imageInfo = data.image_info;

                // Reset crop settings
                cropSettings = { zoom: 100, rotation: 0, offsetX: 0, offsetY: 0 };
                selectedFilter = 'none';

                // Reset image element before showing new one
                previewImage.classList.remove('show');
                previewImage.src = '';

                // Show preview (hides upload prompt) - use original for better quality
                displayImagePreview(originalImageUrl);

                // Show quality indicator
                displayQualityBadge(data.quality);

                // Update upload zone state
                uploadZone.classList.add('has-image');

                // Show edit controls and filters
                editControls.classList.add('show');
                filtersSection.classList.add('show');

                // Generate filter thumbnails (use original for better quality)
                generateFilterThumbnails(originalImageUrl);

                // Reset filter selection
                resetFilterSelection();

                // Enable add to cart
                updateAddToCartButton();

                console.log('[Custom Print] Upload successful:', data);
            } else {
                showError(data.error || 'Upload failed. Please try again.');
            }
        } catch (error) {
            console.error('[Custom Print] Upload error:', error);
            showError('Upload failed. Please check your connection and try again.');
        }

        hideLoading();
    }

    // ============================================
    // Preview Display
    // ============================================
    function displayImagePreview(url) {
        // Hide upload prompt immediately
        if (uploadPrompt) {
            uploadPrompt.classList.add('hidden');
        }

        // Load image and show when ready
        previewImage.onload = function() {
            previewImage.classList.add('show');
            updatePreviewImageClass();
            // Auto-fill: calculate zoom to fill frame without letterboxing
            autoFillImage();
            applyPreviewTransforms();
            // Apply default personalization (border, text) to preview
            updatePersonalizationPreview();
        };

        previewImage.onerror = function() {
            console.error('[Custom Print] Failed to load image:', url);
            // Fallback: try showing anyway
            previewImage.classList.add('show');
        };

        // Enable CORS for canvas operations (thumbnail generation)
        previewImage.crossOrigin = 'anonymous';
        previewImage.src = url;
    }

    function applyPreviewTransforms() {
        if (!previewImage) return;
        // Filter is independent of positioning.
        previewImage.style.filter = (FILTERS && FILTERS[selectedFilter]) || 'none';
        renderImage(uploadZone, previewImage);
    }

    // calculatePreviewBounds is no longer used after the crop refactor —
    // renderImage owns positioning. Kept as a no-op return so any stray
    // legacy callers don't crash.
    function calculatePreviewBounds() {
        return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    function updatePreviewImageClass() {
        if (!previewImage) return;
        // Orientation must come from the loaded <img>'s EXIF-corrected
        // naturalWidth/Height — NOT imageInfo, which may hold raw pre-EXIF
        // dims and would mis-class a portrait phone photo as landscape.
        var w = previewImage.naturalWidth || (imageInfo && imageInfo.width);
        var h = previewImage.naturalHeight || (imageInfo && imageInfo.height);
        if (!w || !h) return;
        previewImage.classList.remove('landscape-img', 'portrait-img');
        previewImage.classList.add(w > h ? 'landscape-img' : 'portrait-img');
    }

    // autoFillImage no longer needed — renderImage's cover-fit baseline
    // (zoom 100 = fully covers the frame) replaces the manual auto-zoom
    // bump that the old transform-based approach required.
    function autoFillImage() { /* no-op */ }

    // ============================================
    // Quality Badge & Modal
    // ============================================
    function displayQualityBadge(quality) {
        qualityBadge.classList.add('show');

        // Robust against the upload endpoint omitting `quality` (or returning
        // it as a primitive) — the old code threw a TypeError trying to read
        // `.quality_percent` off undefined, which left the badge stuck at
        // 0% even when imageInfo had real dimensions to fall back on.
        const serverPct = (quality && typeof quality === 'object') ? quality.quality_percent : 0;
        const percent = serverPct || calculateQualityPercent();

        // Update fill bar
        qualityFill.style.width = Math.min(100, percent) + '%';

        // Remove previous classes
        qualityFill.classList.remove('good', 'warning', 'error');

        if (percent >= 70) {
            qualityFill.classList.add('good');
            qualityText.textContent = `${percent}% - Great quality`;
        } else if (percent >= 50) {
            qualityFill.classList.add('warning');
            qualityText.textContent = `${percent}% - Acceptable`;
        } else {
            qualityFill.classList.add('error');
            qualityText.textContent = `${percent}% - Low quality`;
        }

        // Make badge clickable to show quality modal
        qualityBadge.style.cursor = 'pointer';
        qualityBadge.onclick = showQualityModal;
    }

    function calculateQualityPercent() {
        // Derive from imageInfo when set; otherwise fall back to whatever
        // the <img> reports as its natural size. Prevents the badge from
        // showing 0% just because the upload response didn't include
        // image_info (e.g. TIFFs, where the backend returns 0 dims).
        let w = imageInfo && imageInfo.width;
        let h = imageInfo && imageInfo.height;
        if ((!w || !h) && previewImage && previewImage.naturalWidth) {
            w = previewImage.naturalWidth;
            h = previewImage.naturalHeight;
        }
        if (!w || !h) return 0;

        const req = SIZE_REQUIREMENTS[selectedSize] || SIZE_REQUIREMENTS['30x40'];
        const reqPixels = req.width * req.height;
        const imgPixels = w * h;
        return Math.min(100, Math.round(Math.sqrt(imgPixels / reqPixels) * 100));
    }

    function calculateSizeCompatibility() {
        if (!imageInfo) return {};

        const imgPixels = imageInfo.width * imageInfo.height;
        const compatibility = {};

        Object.keys(SIZE_REQUIREMENTS).forEach(size => {
            const req = SIZE_REQUIREMENTS[size];
            const reqPixels = req.width * req.height;
            const percent = Math.round(Math.sqrt(imgPixels / reqPixels) * 100);

            if (percent >= 80) {
                compatibility[size] = { status: 'good', percent, icon: '✓' };
            } else if (percent >= 50) {
                compatibility[size] = { status: 'warning', percent, icon: '−' };
            } else {
                compatibility[size] = { status: 'error', percent, icon: '✕' };
            }
        });

        return compatibility;
    }

    function showQualityModal() {
        // Create modal if it doesn't exist
        let modal = document.getElementById('quality-modal');
        if (!modal) {
            modal = createQualityModal();
        }

        // Update modal content
        updateQualityModalContent(modal);

        // Show modal
        modal.classList.add('show');
        document.body.style.overflow = 'hidden';
    }

    function createQualityModal() {
        const modal = document.createElement('div');
        modal.id = 'quality-modal';
        modal.className = 'quality-modal';
        modal.innerHTML = `
            <div class="quality-modal-content">
                <div class="quality-modal-header">
                    <span class="quality-modal-title">Image Quality</span>
                    <button type="button" class="quality-modal-close" id="quality-modal-close">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
                <div class="quality-modal-body">
                    <p class="quality-modal-intro">
                        If you upload a lower-resolution image, the final product may appear blurry.
                        See below which poster sizes are fully supported by your current image.
                    </p>
                    <div class="size-compatibility-list" id="size-compatibility-list"></div>
                    <div class="upscale-upsell" id="upscale-upsell">
                        <div class="upscale-upsell-header">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                            </svg>
                            <span>Enhance your image</span>
                        </div>
                        <p class="upscale-upsell-text">
                            Our team can professionally enhance your image to support larger print sizes for an additional £2.99.
                        </p>
                        <button type="button" class="upscale-btn" id="upscale-btn">
                            Add Enhancement (+£2.99)
                        </button>
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Close handlers
        modal.querySelector('#quality-modal-close').addEventListener('click', closeQualityModal);
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeQualityModal();
        });

        // Upscale button handler
        modal.querySelector('#upscale-btn').addEventListener('click', handleUpscaleRequest);

        return modal;
    }

    function updateQualityModalContent(modal) {
        const listContainer = modal.querySelector('#size-compatibility-list');
        const upsellSection = modal.querySelector('#upscale-upsell');
        const compatibility = calculateSizeCompatibility();

        // Build size list
        let html = '';
        let hasLowQuality = false;

        Object.keys(SIZE_REQUIREMENTS).forEach(size => {
            const info = compatibility[size];
            const label = SIZE_REQUIREMENTS[size].label;
            const statusClass = info.status;

            if (info.status !== 'good') hasLowQuality = true;

            html += `
                <div class="size-compatibility-item ${statusClass}">
                    <span class="size-icon ${statusClass}">${info.icon}</span>
                    <span class="size-label">${label}</span>
                </div>
            `;
        });

        listContainer.innerHTML = html;

        // Show/hide upsell based on quality
        upsellSection.style.display = hasLowQuality ? 'block' : 'none';
    }

    function closeQualityModal() {
        const modal = document.getElementById('quality-modal');
        if (modal) {
            modal.classList.remove('show');
            document.body.style.overflow = '';
        }
    }

    // Upscale service product variant ID
    // Injected from Liquid (preferred) so this doesn't break if variants change.
    // Fallback keeps legacy behavior.
    const UPSCALE_SERVICE_VARIANT_ID = window.customPrintAddonVariantId || 56569657295225;
    let upscaleRequested = false;

    function handleUpscaleRequest() {
        // Toggle: if enhancement is already added, remove it; otherwise add it.
        // Previously this was one-way (button got disabled forever) which left
        // users no way to back out after clicking.
        if (upscaleRequested) {
            upscaleRequested = false;
            resetUpscaleButton();
            return;
        }

        upscaleRequested = true;

        const btn = document.getElementById('upscale-btn');
        if (btn) {
            btn.textContent = '✓ Enhancement Added — click to remove';
            btn.style.background = '#22c55e';
            btn.disabled = false;
        }

        updatePriceDisplay();

        setTimeout(() => {
            closeQualityModal();
        }, 800);
    }

    // Helper function to reset upscale button state
    function resetUpscaleButton() {
        const btn = document.getElementById('upscale-btn');
        if (btn) {
            btn.textContent = 'Add Enhancement (+£2.99)';
            btn.style.background = '#633e29';
            btn.disabled = false;
        }
        // Update price display to remove enhancement fee
        updatePriceDisplay();
    }

    // Check if enhancement service is already in cart for this order
    async function checkEnhancementInCart(orderIdToCheck) {
        try {
            const response = await fetch('/cart.js');
            if (!response.ok) return;

            const cart = await response.json();

            // Look for enhancement service with matching OrderID
            const hasEnhancement = cart.items.some(item => {
                const itemOrderId = item.properties?._OrderID || item.properties?.['For Order'];
                const isEnhancement = (item.product_title || item.title || '').toLowerCase().includes('enhancement') ||
                                     (item.product_title || item.title || '').toLowerCase().includes('upscale');
                return isEnhancement && itemOrderId === orderIdToCheck;
            });

            if (hasEnhancement) {
                console.log('[Custom Print] Enhancement service found in cart for order:', orderIdToCheck);
                upscaleRequested = true;

                // Reflect added-state, but keep clickable so the user can
                // toggle it off (handleUpscaleRequest treats a second click
                // as a removal).
                const btn = document.getElementById('upscale-btn');
                if (btn) {
                    btn.textContent = '✓ Enhancement Added — click to remove';
                    btn.style.background = '#22c55e';
                    btn.disabled = false;
                }

                // Update price display
                updatePriceDisplay();
            } else {
                console.log('[Custom Print] No enhancement service in cart for order:', orderIdToCheck);
            }
        } catch (error) {
            console.error('[Custom Print] Error checking cart for enhancement:', error);
        }
    }

    // Helper function to add upscale service to cart
    async function addUpscaleServiceToCart() {
        if (!upscaleRequested) return;

        try {
            // First check if enhancement already in cart for this order (avoid duplicates)
            const cartResponse = await fetch('/cart.js');
            if (cartResponse.ok) {
                const cart = await cartResponse.json();
                const alreadyInCart = cart.items.some(item => {
                    const itemOrderId = item.properties?._OrderID || item.properties?.['For Order'];
                    const isEnhancement = (item.product_title || item.title || '').toLowerCase().includes('enhancement') ||
                                         (item.product_title || item.title || '').toLowerCase().includes('upscale');
                    return isEnhancement && itemOrderId === orderId;
                });

                if (alreadyInCart) {
                    console.log('[Custom Print] Enhancement service already in cart for order:', orderId);
                    return; // Skip adding duplicate
                }
            }

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: UPSCALE_SERVICE_VARIANT_ID,
                    quantity: 1,
                    properties: {
                        '_OrderID': orderId,
                        'For Order': orderId
                    }
                })
            });

            if (!response.ok) {
                console.error('[Custom Print] Failed to add upscale service to cart');
            } else {
                console.log('[Custom Print] Upscale service added to cart for order:', orderId);
            }
        } catch (error) {
            console.error('[Custom Print] Error adding upscale service:', error);
        }
    }

    function revalidateQuality() {
        if (!imageInfo) return;

        const percent = calculateQualityPercent();
        displayQualityBadge({ quality_percent: percent });
    }

    // ============================================
    // Filters
    // ============================================
    function setupFilters() {
        const filterOptions = document.querySelectorAll('.filter-option');

        filterOptions.forEach(option => {
            option.addEventListener('click', () => {
                // Remove active from all
                filterOptions.forEach(o => o.classList.remove('active'));

                // Add active to clicked
                option.classList.add('active');

                // Update selected filter
                selectedFilter = option.dataset.filter;

                // Apply to preview
                applyPreviewTransforms();

                console.log('[Custom Print] Filter selected:', selectedFilter);
            });
        });
    }

    function resetFilterSelection() {
        const filterOptions = document.querySelectorAll('.filter-option');
        filterOptions.forEach(o => o.classList.remove('active'));

        const noneOption = document.querySelector('.filter-option[data-filter="none"]');
        if (noneOption) noneOption.classList.add('active');

        selectedFilter = 'none';
    }

    function generateFilterThumbnails(imageUrl) {
        // Thumbs render at 52px (see .filter-thumb in sections/custom-print.liquid).
        // Ask the CDN for a retina-covering width instead of shipping the
        // full-resolution editing image into six tiny swatches.
        const thumbUrl = imageUrl + (imageUrl.includes('?') ? '&' : '?') + 'width=96';

        // Create thumbnails with filters applied
        Object.keys(FILTERS).forEach(filterName => {
            const thumbContainer = document.getElementById('filter-thumb-' + filterName);
            if (thumbContainer) {
                // Clear previous content
                thumbContainer.innerHTML = '';

                // Create image element
                const img = document.createElement('img');
                img.src = thumbUrl;
                img.style.filter = FILTERS[filterName];
                img.alt = filterName;

                thumbContainer.appendChild(img);
            }
        });
    }

    // ============================================
    // Edit Controls
    // ============================================
    function setupEditControls() {
        const cropBtn = document.getElementById('crop-btn');
        const replaceBtn = document.getElementById('replace-btn');

        if (cropBtn) {
            cropBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                openCropModal();
            });
        }

        if (replaceBtn) {
            replaceBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                fileInput.click();
            });
        }

        // The sample-photo nudge fires the same action as NEW IMAGE.
        const sampleNudge = document.getElementById('cp-sample-nudge');
        if (sampleNudge) {
            sampleNudge.addEventListener('click', (e) => {
                e.stopPropagation();
                fileInput.click();
            });
        }
    }

    // ============================================
    // Crop Modal
    // ============================================
    function setupCropModal() {
        // Close modal
        if (cropModalClose) {
            cropModalClose.addEventListener('click', closeCropModal);
        }

        // Click outside to close
        if (cropModal) {
            cropModal.addEventListener('click', (e) => {
                if (e.target === cropModal) {
                    closeCropModal();
                }
            });
        }

        // Zoom slider
        if (zoomSlider) {
            zoomSlider.addEventListener('input', (e) => {
                cropSettings.zoom = parseInt(e.target.value);
                // Constrain offset when zoom changes (less zoom = less room to move)
                constrainCurrentOffset();
            });
        }

        // Rotate button
        if (rotateBtn) {
            rotateBtn.addEventListener('click', () => {
                cropSettings.rotation = (cropSettings.rotation + 90) % 360;

                // Recalculate auto-zoom since rotation affects dimensions
                const autoZoom = calculateAutoZoomForOrientation();
                if (autoZoom > cropSettings.zoom) {
                    cropSettings.zoom = autoZoom;
                    if (zoomSlider) {
                        zoomSlider.value = autoZoom;
                        zoomSlider.min = Math.max(100, autoZoom);
                    }
                    console.log('[Custom Print] Auto-zoom after rotation:', autoZoom + '%');
                } else if (autoZoom <= 100) {
                    // Reset slider min if no auto-zoom needed
                    if (zoomSlider) zoomSlider.min = 100;
                }

                applyCropTransforms();
            });
        }

        // Orientation buttons — there are now sidebar buttons (always
        // visible) AND modal buttons may have been removed; this delegated
        // handler covers any .orientation-btn and re-renders both the
        // crop modal preview (no-op when modal is closed) and the main
        // print preview.
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('.orientation-btn[data-orientation]');
            if (!btn) return;
            const newOrientation = btn.dataset.orientation;
            if (newOrientation === selectedOrientation) return;
            selectedOrientation = newOrientation;
            // Sync active state across every set of orientation buttons.
            document.querySelectorAll('.orientation-btn[data-orientation]').forEach(b => {
                b.classList.toggle('active', b.dataset.orientation === selectedOrientation);
            });
            updateCropPreviewAspect();        // crop modal frame
            updateMainPreviewOrientation();   // main preview frame aspect class
            requestAnimationFrame(applyPreviewTransforms); // re-render main preview after aspect settles
        });

        // Confirm button
        if (cropConfirmBtn) {
            cropConfirmBtn.addEventListener('click', () => {
                // First update orientation (changes aspect ratio)
                updateMainPreviewOrientation();
                // Wait for DOM to update before applying transforms with new bounds
                setTimeout(() => {
                    // Ensure image fills frame (auto-fill recalculates zoom if needed)
                    autoFillImage();
                    applyPreviewTransforms();
                }, 50);
                closeCropModal();
            });
        }

        // Upload new from modal
        if (cropUploadNew) {
            cropUploadNew.addEventListener('click', (e) => {
                e.stopPropagation();
                closeCropModal();
                fileInput.click();
            });
        }

        // Drag to pan
        if (cropPreviewArea) {
            cropPreviewArea.addEventListener('mousedown', startDrag);
            cropPreviewArea.addEventListener('touchstart', startDrag, { passive: false });

            document.addEventListener('mousemove', onDrag);
            document.addEventListener('touchmove', onDrag, { passive: false });

            document.addEventListener('mouseup', endDrag);
            document.addEventListener('touchend', endDrag);
        }
    }

    function openCropModal() {
        if (!originalImageUrl) return;

        // Wipe any inline state the image picked up from previous sessions.
        // The legacy version corrupted itself on re-open by relying on
        // residual transform/class state for measurements; renderImage is
        // pure-geometry, so a clean slate is sufficient.
        cropPreviewImage.removeAttribute('style');
        cropPreviewImage.classList.remove('landscape-img', 'portrait-img');
        if (cropPreviewArea) cropPreviewArea.classList.remove('portrait', 'landscape');

        cropPreviewImage.src = originalImageUrl;

        cropModal.classList.add('show');
        document.body.style.overflow = 'hidden';

        const init = () => {
            // Frame aspect (orientation class drives the .crop-preview-area
            // CSS aspect-ratio var).
            cropPreviewArea.classList.add(selectedOrientation === 'landscape' ? 'landscape' : 'portrait');

            // Sync slider to current saved zoom (clamped into slider range).
            if (zoomSlider) {
                const sMin = parseFloat(zoomSlider.min) || 100;
                const sMax = parseFloat(zoomSlider.max) || 200;
                let z = Number.isFinite(cropSettings.zoom) ? cropSettings.zoom : 100;
                z = Math.min(sMax, Math.max(sMin, z));
                cropSettings.zoom = z;
                zoomSlider.value = z;
            }

            // Wait one paint so the area's aspect-ratio settles, then render.
            requestAnimationFrame(() => renderImage(cropPreviewArea, cropPreviewImage));
        };

        if (cropPreviewImage.complete && cropPreviewImage.naturalWidth > 0) {
            // Image cached — still wait one rAF so the modal has actually
            // painted before we measure clientWidth/Height.
            requestAnimationFrame(init);
        } else {
            cropPreviewImage.onload = () => requestAnimationFrame(init);
        }
    }

    function closeCropModal() {
        cropModal.classList.remove('show');
        document.body.style.overflow = '';
    }

    function applyCropTransforms() {
        renderImage(cropPreviewArea, cropPreviewImage);
    }

    function updateCropPreviewAspect() {
        if (!cropPreviewArea) return;

        // Use CSS classes for orientation
        cropPreviewArea.classList.remove('portrait', 'landscape');
        if (selectedOrientation === 'portrait') {
            cropPreviewArea.classList.add('portrait');
        } else {
            cropPreviewArea.classList.add('landscape');
        }

        // Wait for DOM to update with new dimensions, then auto-zoom if needed
        setTimeout(() => {
            // Auto-zoom to fill frame when image orientation doesn't match frame
            const autoZoom = calculateAutoZoomForOrientation();
            const minZoom = Math.max(100, autoZoom);

            console.log('[Custom Print] Orientation change - auto-zoom:', autoZoom);

            if (zoomSlider) {
                zoomSlider.min = minZoom;
                if (cropSettings.zoom < minZoom) {
                    cropSettings.zoom = minZoom;
                }
                zoomSlider.value = cropSettings.zoom;
            }

            // Reset offset and center the image
            cropSettings.offsetX = 0;
            cropSettings.offsetY = 0;

            // Apply transforms
            applyCropTransforms();

            // Verify position after render
            setTimeout(() => {
                verifyAndCorrectPosition();
            }, 100);
        }, 100);
    }

    /**
     * Calculate the minimum zoom needed to fill the frame when image orientation
     * doesn't match the selected orientation (e.g., portrait image in landscape frame)
     * Uses actual rendered dimensions when available for accuracy on mobile
     */
    // calculateAutoZoomForOrientation is no longer needed. The slider min
    // is always 100 (= cover-fit baseline) because renderImage already
    // covers the frame at zoom 100 regardless of frame/image orientation.
    function calculateAutoZoomForOrientation() {
        return 100;
    }

    function updateMainPreviewOrientation() {
        // Update the main preview container's aspect ratio
        if (uploadZone) {
            uploadZone.classList.remove('landscape');
            if (selectedOrientation === 'landscape') {
                uploadZone.classList.add('landscape');
            }
        }
    }

    /**
     * Updates the aspect ratio of preview containers based on selected size
     * @param {string} size - Size code (e.g., 'A4', '30x40', '40x50', '50x70', '60x80')
     */
    function updatePreviewRatio(size) {
        const ratio = SIZE_RATIOS[size] || SIZE_RATIOS['30x40']; // Default to 30x40
        const landscapeRatio = 1 / ratio;

        console.log('[Custom Print] Updating aspect ratio for size:', size, 'ratio:', ratio.toFixed(3));

        // Wrap/bundle are priced by the size being wrapped — re-stamp the gift
        // pills and the "+ £x wrap" price line for the size just chosen.
        if (typeof updateGiftPriceLine === 'function') updateGiftPriceLine();

        // Update all preview containers with CSS custom properties
        const elements = [uploadZone, cropPreviewArea, lightboxFrame];
        elements.forEach(el => {
            if (el) {
                el.style.setProperty('--portrait-ratio', ratio);
                el.style.setProperty('--landscape-ratio', landscapeRatio);
            }
        });

        // If image is loaded, reflow transforms WITHOUT changing zoom level
        // (size changes should not auto-zoom the image)
        if (imageInfo && uploadedImage) {
            // Wait for DOM to update with new aspect ratio, then re-apply transforms
            setTimeout(() => {
                applyPreviewTransforms();

                // If crop modal is open, also update crop preview
                if (cropModal && cropModal.classList.contains('show')) {
                    applyCropTransforms();
                }
            }, 50);
        }
    }

    function centerCropImage() {
        // Reset to center position
        cropSettings.offsetX = 0;
        cropSettings.offsetY = 0;

        // Apply transforms after a short delay to ensure DOM has updated
        setTimeout(() => {
            applyCropTransforms();
        }, 50);
    }

    function constrainCurrentOffset() {
        // Ensure current offset percentage is within bounds (-100 to 100)
        cropSettings.offsetX = Math.max(-100, Math.min(100, cropSettings.offsetX));
        cropSettings.offsetY = Math.max(-100, Math.min(100, cropSettings.offsetY));
        applyCropTransforms();
    }

    // Drag state - store overflow values at drag start
    let dragStartOverflowX = 0;
    let dragStartOverflowY = 0;

    // Drag handlers for pan
    function startDrag(e) {
        e.preventDefault();
        isDragging = true;

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        dragStartX = clientX;
        dragStartY = clientY;
        dragStartOffsetX = cropSettings.offsetX;
        dragStartOffsetY = cropSettings.offsetY;

        // Capture actual rendered overflow at drag start
        const imgRect = cropPreviewImage.getBoundingClientRect();
        const frameRect = cropPreviewArea.getBoundingClientRect();
        dragStartOverflowX = Math.max(0, (imgRect.width - frameRect.width) / 2);
        dragStartOverflowY = Math.max(0, (imgRect.height - frameRect.height) / 2);

        cropPreviewImage.style.cursor = 'grabbing';
    }

    function onDrag(e) {
        if (!isDragging) return;
        e.preventDefault();

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        // Calculate delta from drag start in pixels
        const deltaX = clientX - dragStartX;
        const deltaY = clientY - dragStartY;

        // Use the overflow values captured at drag start for consistent calculations
        const overflowX = dragStartOverflowX;
        const overflowY = dragStartOverflowY;

        // Minimum threshold for allowing panning
        const minOverflowThreshold = 5;

        // Calculate new offset directly in pixels, then constrain
        let newOffsetX = dragStartOffsetX;
        let newOffsetY = dragStartOffsetY;

        if (overflowX > minOverflowThreshold) {
            // Convert pixel delta to percentage of overflow
            const deltaPercent = (deltaX / overflowX) * 100;
            newOffsetX = dragStartOffsetX + deltaPercent;
            // Constrain to -100% to 100%
            newOffsetX = Math.max(-100, Math.min(100, newOffsetX));
        } else {
            // No meaningful horizontal overflow - force center
            newOffsetX = 0;
        }

        if (overflowY > minOverflowThreshold) {
            const deltaPercent = (deltaY / overflowY) * 100;
            newOffsetY = dragStartOffsetY + deltaPercent;
            newOffsetY = Math.max(-100, Math.min(100, newOffsetY));
        } else {
            // No meaningful vertical overflow - force center
            newOffsetY = 0;
        }

        cropSettings.offsetX = newOffsetX;
        cropSettings.offsetY = newOffsetY;

        applyCropTransforms();

        // REACTIVE FIX: After applying, verify no empty space and correct if needed
        verifyAndCorrectPosition();
    }

    // verifyAndCorrectPosition / applyCropTransformsWithoutVerify /
    // calculateCropBounds — no longer needed. renderImage's offset clamp
    // (-1 to 1 of halfOverflow) prevents any "empty space" condition by
    // construction, so the reactive correction loop is unnecessary.
    function verifyAndCorrectPosition() { /* no-op */ }
    function applyCropTransformsWithoutVerify() { renderImage(cropPreviewArea, cropPreviewImage); }
    function calculateCropBounds() {
        return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    function endDrag() {
        isDragging = false;
        if (cropPreviewImage) {
            cropPreviewImage.style.cursor = 'move';
        }
    }

    // ============================================
    // Lightbox Modal
    // ============================================
    // Tab toggle for the two-step sidebar (Customise / Product & Size).
    function setupStepTabs() {
        const tabs = document.querySelectorAll('.cp-step-tab[data-step]');
        const steps = document.querySelectorAll('.cp-step[data-step-content]');
        if (!tabs.length || !steps.length) return;
        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const target = tab.dataset.step;
                tabs.forEach(t => t.classList.toggle('active', t === tab));
                steps.forEach(s => s.classList.toggle('active', s.dataset.stepContent === target));
            });
        });
    }

    function setupLightbox() {
        // Click on preview to open lightbox (only when image is uploaded)
        if (uploadZone) {
            uploadZone.addEventListener('click', (e) => {
                // Don't open lightbox if clicking on upload prompt, file input, edit controls, or if no image
                if (!uploadedImage ||
                    e.target.closest('.upload-prompt') ||
                    e.target.closest('.edit-controls') ||
                    e.target.closest('.edit-btn') ||
                    e.target.id === 'image-upload' ||
                    e.target.tagName === 'INPUT') return;
                openLightbox();
            });
        }

        // Close button
        if (lightboxClose) {
            lightboxClose.addEventListener('click', closeLightbox);
        }

        // Click outside to close
        if (lightboxModal) {
            lightboxModal.addEventListener('click', (e) => {
                if (e.target === lightboxModal) {
                    closeLightbox();
                }
            });
        }

        // Escape key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && lightboxModal && lightboxModal.classList.contains('show')) {
                closeLightbox();
            }
        });

        // Recalculate transforms on resize
        window.addEventListener('resize', () => {
            if (lightboxModal && lightboxModal.classList.contains('show')) {
                setTimeout(applyLightboxTransforms, 100);
            }
        });
    }

    function openLightbox() {
        if (!uploadedImage || !originalImageUrl) return;

        // Set image source
        lightboxImage.src = originalImageUrl;

        // Wait for image to load before applying transforms
        lightboxImage.onload = function() {
            // Apply orientation class to frame
            lightboxFrame.classList.remove('landscape');
            if (selectedOrientation === 'landscape') {
                lightboxFrame.classList.add('landscape');
            }

            // Apply image orientation class
            lightboxImage.classList.remove('landscape-img', 'portrait-img');
            if (imageInfo && imageInfo.width > imageInfo.height) {
                lightboxImage.classList.add('landscape-img');
            } else {
                lightboxImage.classList.add('portrait-img');
            }

            // Apply filter
            lightboxImage.style.filter = FILTERS[selectedFilter];

            // Apply border styling
            applyLightboxBorder();

            // Apply personalization text
            applyLightboxPersonalization();

            // Apply transforms after a short delay for DOM to update
            setTimeout(applyLightboxTransforms, 50);
        };

        // Show modal
        lightboxModal.classList.add('show');
        document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
        if (lightboxModal) {
            lightboxModal.classList.remove('show');
            document.body.style.overflow = '';
        }
    }

    function applyLightboxTransforms() {
        if (!lightboxImage || !lightboxFrame) return;

        const scale = cropSettings.zoom / 100;
        const rotate = cropSettings.rotation;

        // Calculate bounds for lightbox frame
        const bounds = calculateLightboxBounds();

        // Minimum overflow threshold (must match crop modal)
        const minOverflowThreshold = 5;

        // Convert percentage offset to pixels
        const offsetXPx = bounds.maxX > minOverflowThreshold ? (cropSettings.offsetX / 100) * bounds.maxX : 0;
        const offsetYPx = bounds.maxY > minOverflowThreshold ? (cropSettings.offsetY / 100) * bounds.maxY : 0;

        // Apply transform
        lightboxImage.style.transform = `translate(-50%, -50%) translate(${offsetXPx}px, ${offsetYPx}px) scale(${scale}) rotate(${rotate}deg)`;
    }

    function calculateLightboxBounds() {
        if (!imageInfo || !lightboxFrame) {
            return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
        }

        const scale = cropSettings.zoom / 100;

        // Get frame dimensions (use clientWidth/clientHeight to exclude borders)
        const frameWidth = lightboxFrame.clientWidth;
        const frameHeight = lightboxFrame.clientHeight;

        if (!frameWidth || !frameHeight) {
            return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
        }

        const frameRatio = frameWidth / frameHeight;

        // IMPORTANT: CSS sizes based on ORIGINAL image dimensions (via class),
        // then rotation is applied as a visual transform.
        const origWidth = imageInfo.width;
        const origHeight = imageInfo.height;
        const origRatio = origWidth / origHeight;

        const isOrigLandscape = origRatio > 1;
        const isPortraitFrame = frameRatio < 1;

        // Calculate displayed dimensions based on CSS rules (using ORIGINAL ratio)
        let displayWidth, displayHeight;

        if (isOrigLandscape && isPortraitFrame) {
            displayHeight = frameHeight * scale;
            displayWidth = displayHeight * origRatio;
        } else if (!isOrigLandscape && !isPortraitFrame) {
            displayWidth = frameWidth * scale;
            displayHeight = displayWidth / origRatio;
        } else if (isOrigLandscape && !isPortraitFrame) {
            displayHeight = frameHeight * scale;
            displayWidth = displayHeight * origRatio;
        } else {
            displayWidth = frameWidth * scale;
            displayHeight = displayWidth / origRatio;
        }

        // If rotated 90 or 270 degrees, swap visual dimensions
        if (cropSettings.rotation === 90 || cropSettings.rotation === 270) {
            [displayWidth, displayHeight] = [displayHeight, displayWidth];
        }

        const overflowX = Math.max(0, (displayWidth - frameWidth) / 2);
        const overflowY = Math.max(0, (displayHeight - frameHeight) / 2);

        return {
            minX: -overflowX,
            maxX: overflowX,
            minY: -overflowY,
            maxY: overflowY
        };
    }

    function applyLightboxBorder() {
        if (!lightboxFrame) return;

        const borderColors = {
            white: '#ffffff',
            beige: '#f5f0e6',
            black: '#0a0a0a',
            'dark-brown': '#1a1310',
            none: 'transparent'
        };

        const hasBorder = personalization.border !== 'none';
        const borderColor = borderColors[personalization.border] || borderColors.white;

        // Apply border
        lightboxFrame.classList.toggle('has-border', hasBorder);
        lightboxFrame.style.borderColor = borderColor;

        // Update background color
        lightboxFrame.style.backgroundColor = hasBorder ? borderColor : '#1a1a2e';
    }

    function applyLightboxPersonalization() {
        if (!lightboxOverlay || !lightboxHeadline || !lightboxCaption) return;

        const hasText = personalization.headline || personalization.caption;

        // Update text content
        lightboxHeadline.textContent = personalization.headline || '';
        lightboxCaption.textContent = personalization.caption || '';

        // Apply font style
        const fontFamily = resolveFontFamily(personalization.font);
        lightboxHeadline.style.fontFamily = fontFamily;
        lightboxCaption.style.fontFamily = fontFamily;

        // Determine text color
        const hasBorder = personalization.border !== 'none';
        let textColor;

        if (!hasBorder) {
            // No border - use text color selection
            if (personalization.textColor === 'auto') {
                const detectedColor = detectImageBrightness(lightboxImage);
                textColor = detectedColor === 'white' ? '#ffffff' : '#0a0a0a';
            } else {
                textColor = personalization.textColor === 'white' ? '#ffffff' : '#0a0a0a';
            }
            // Add text shadow for readability
            const shadowColor = textColor === '#ffffff' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)';
            lightboxHeadline.style.textShadow = `0 1px 3px ${shadowColor}`;
            lightboxCaption.style.textShadow = `0 1px 3px ${shadowColor}`;
        } else {
            // Has border - use contrast based on border color
            textColor = (personalization.border === 'black' || personalization.border === 'dark-brown') ? '#ffffff' : '#0a0a0a';
            lightboxHeadline.style.textShadow = 'none';
            lightboxCaption.style.textShadow = 'none';
        }

        lightboxHeadline.style.color = textColor;
        lightboxCaption.style.color = textColor;

        // Set overlay background
        const borderColors = {
            white: '#ffffff',
            beige: '#f5f0e6',
            black: '#0a0a0a',
            'dark-brown': '#1a1310',
            none: 'transparent'
        };
        lightboxOverlay.style.backgroundColor = borderColors[personalization.border] || 'transparent';

        // Show/hide overlay
        lightboxOverlay.classList.toggle('show', hasText);
    }

    // ============================================
    // Product Pickers
    // ============================================
    function setupProductPickers() {
        // Product type picker
        setupPicker('#product-type-picker .elem__picker', (element) => {
            selectedType = element.dataset.option;
            updateFrameVisibility();
            updateSizePickerPrices();
            updateFramePickerAvailability();
            updateVariantId();
        });

        // Size picker — picking a size can change which frame colours are
        // available (e.g. Dark Brown only ships in A4, so picking 50×70
        // greys out Dark Brown in the frame picker).
        setupPicker('#size-picker .elem__picker', (element) => {
            selectedSize = element.dataset.sizeCode || '30x40';
            updatePrice(element.dataset.price);
            updateFramePickerAvailability();
            updateVariantId();

            // Update preview aspect ratio for new size
            updatePreviewRatio(selectedSize);

            // Re-validate quality for new size
            if (imageInfo) {
                revalidateQuality();
            }
        });

        // Frame picker — picking a frame colour can change which sizes
        // are available (Dark Brown only exists for A4).
        setupPicker('#frame-picker .elem__picker', (element) => {
            selectedFrame = element.dataset.option;
            updatePreviewFrame(element.dataset.frame);
            updateSizePickerPrices();
            updateVariantId();
        });

        // Gift wrapping picker — None / Ribbon / Gift wrap / Bundle.
        setupPicker('#cp-gift-picker .elem__picker', (element) => {
            selectedGift = element.dataset.gift || 'none';
            updateGiftUI();
        });

        // Festive paper design swatches (gift-paper-picker snippet), only
        // meaningful for Gift wrap / Bundle; visibility is driven by
        // updateGiftUI(). Remembered per order id so a return visit (or the
        // edit-mode hydrate below) restores the customer's chosen design.
        cpPaperPicker = window.SkylitGifts
            ? window.SkylitGifts.bindPaperPicker(document.getElementById('cp-gift-paper'), (id) => {
                selectedPaper = id;
                if (window.SkylitGifts) window.SkylitGifts.rememberPaper('cp_' + orderId, id);
                updateGiftUI();
              })
            : null;
        selectedPaper = (window.SkylitGifts && window.SkylitGifts.recallPaperFor('cp_' + orderId)) || 'dots';
        if (cpPaperPicker) cpPaperPicker.set(selectedPaper);

        cpRibbonPicker = window.SkylitGifts
            ? window.SkylitGifts.bindRibbonPicker(document.getElementById('cp-gift-ribbon'), (name) => {
                selectedRibbon = name;
                if (window.SkylitGifts) window.SkylitGifts.rememberRibbon('cp_' + orderId, name);
                updateGiftUI();
              })
            : null;
        selectedRibbon = (window.SkylitGifts && window.SkylitGifts.recallRibbonFor('cp_' + orderId)) || 'Red';
        if (cpRibbonPicker) cpRibbonPicker.set(selectedRibbon);
    }

    // ============================================
    // Gift wrapping
    // ============================================
    // Reads the gift-note-card snippet's DOM state (it's in local_only
    // mode, so values only live in the DOM until ATC writes them as
    // line-item properties on the paired add-on).
    function readGiftDom() {
        const out = { note: '', design: 'editorial', font: 'handwritten', enabled: false };
        try {
            const ta = document.getElementById('Gift-note-customprint');
            if (ta) out.note = ta.value || '';
            const root = document.querySelector('.gnc[data-gnc="customprint"]');
            if (root) {
                const d = root.getAttribute('data-design');
                if (d === 'editorial' || d === 'minimal') out.design = d;
                const f = root.getAttribute('data-font');
                if (f === 'handwritten' || f === 'classic') out.font = f;
                out.enabled = root.getAttribute('data-enabled') === 'true';
            }
        } catch (e) {}
        return out;
    }

    // Sync gift UI to selectedGift + the current product type:
    //  - flat Print ships rolled → only Ribbon applies; hide Wrap/Bundle
    //  - note editor + overlay card show only for ribbon/bundle
    //  - ribbon/giftwrap imagery overlays toggle on the preview
    function updateGiftUI() {
        const isFramed = selectedType.toLowerCase().includes('framed');
        const isCanvas = selectedType.toLowerCase().includes('canvas');
        const isFlatPrint = !isFramed && !isCanvas;

        // Hide Wrap + Bundle tiles for flat prints; demote if one was active.
        document.querySelectorAll('#cp-gift-picker [data-hide-for-print="true"]').forEach((tile) => {
            tile.hidden = isFlatPrint;
        });

        // Show the "ships rolled" tooltip on the Ribbon tile only for flat Print.
        const giftPicker = document.getElementById('cp-gift-picker');
        if (giftPicker) giftPicker.classList.toggle('cp-gift_picker--print', isFlatPrint);

        if (isFlatPrint && (selectedGift === 'giftwrap' || selectedGift === 'bundle')) {
            selectedGift = 'ribbon';
            document.querySelectorAll('#cp-gift-picker .elem__picker').forEach((el) => {
                el.classList.toggle('active__picker', el.dataset.gift === 'ribbon');
            });
        }

        const ribbonOn = selectedGift === 'ribbon' || selectedGift === 'bundle';
        const wrapOn = selectedGift === 'giftwrap' || selectedGift === 'bundle';

        // Gift-message editor — only meaningful with a ribbon to pin a card to.
        const noteWrap = document.getElementById('cp-gift-note-wrap');
        if (noteWrap) noteWrap.setAttribute('data-gift-note-visible', ribbonOn ? 'true' : 'false');

        // Preview overlays.
        const ribbonOverlay = document.getElementById('cp-ribbon-overlay');
        const wrapOverlay = document.getElementById('cp-giftwrap-overlay');
        const cardOverlay = document.getElementById('cp-giftcard-overlay');
        if (ribbonOverlay) {
            ribbonOverlay.classList.toggle('visible', ribbonOn);
            if (window.SkylitGifts) {
                const ribbonSrc = window.SkylitGifts.ribbonImageFor(selectedRibbon || 'Red');
                if (ribbonOverlay.getAttribute('src') !== ribbonSrc) ribbonOverlay.setAttribute('src', ribbonSrc);
            }
        }
        if (wrapOverlay) wrapOverlay.classList.toggle('visible', wrapOn);
        if (cardOverlay) cardOverlay.classList.toggle('visible', ribbonOn);

        const paperWrap = document.getElementById('cp-gift-paper-wrap');
        if (paperWrap) paperWrap.setAttribute('data-gift-paper-visible', wrapOn ? 'true' : 'false');
        const ribbonWrap = document.getElementById('cp-ribbon-color-wrap');
        if (ribbonWrap) ribbonWrap.setAttribute('data-ribbon-color-visible', ribbonOn ? 'true' : 'false');
        if (wrapOverlay && window.SkylitGifts) window.SkylitGifts.applyPaperTexture(wrapOverlay, selectedPaper);

        // Price line — fold the wrapping add-on into the displayed total.
        updateGiftPriceLine();
    }

    // Edit-mode: restore gift state from the paired add-on already in the
    // cart for this orderId. Maps the add-on's variant id back to a gift
    // kind, restores the note/design/font into the snippet's DOM.
    async function hydrateGiftFromCart() {
        try {
            const cart = await fetch('/cart.js?t=' + Date.now()).then(r => r.json());
            const paired = (cart.items || []).find(
                (li) => li.properties && li.properties._paired_with_order_id === orderId
            );
            if (!paired) return;

            let kind = 'none';
            if (paired.variant_id === CP_GIFT_IDS.ribbon) kind = 'ribbon';
            else if (paired.variant_id === CP_GIFT_IDS.giftwrap) kind = 'giftwrap';
            else if (paired.variant_id === CP_GIFT_IDS.bundle) kind = 'bundle';
            kind = (window.SkylitGifts && window.SkylitGifts.kindFor(paired.variant_id)) || kind;
            if (kind === 'none') return;

            selectedGift = kind;
            document.querySelectorAll('#cp-gift-picker .elem__picker').forEach((el) => {
                el.classList.toggle('active__picker', el.dataset.gift === kind);
            });

            const pd = window.SkylitGifts && window.SkylitGifts.designFor(paired.variant_id);
            if (pd) { selectedPaper = pd; if (cpPaperPicker) cpPaperPicker.set(pd); }

            // Restore note/design/font into the gift-note-card snippet DOM.
            const p = paired.properties || {};
            if (p._ribbon_color && window.SkylitGifts) {
                selectedRibbon = window.SkylitGifts.ribbonColorKey(p._ribbon_color);
                if (cpRibbonPicker) cpRibbonPicker.set(selectedRibbon);
            }
            const note = p._gift_note || '';
            let design = p._gift_card_design;
            if (design !== 'minimal' && design !== 'editorial') design = 'editorial';
            let font = p._gift_card_font;
            if (font !== 'handwritten' && font !== 'classic') font = 'handwritten';
            document.querySelectorAll('.gnc[data-gnc="customprint"]').forEach((el) => {
                el.setAttribute('data-design', design);
                el.setAttribute('data-font', font);
                el.setAttribute('data-enabled', note ? 'true' : 'false');
            });
            const ta = document.getElementById('Gift-note-customprint');
            if (ta) {
                ta.value = note;
                ta.dispatchEvent(new Event('input', { bubbles: true }));
            }
            const cb = document.querySelector('.gnc[data-gnc="customprint"] [data-gnc-enabled]');
            if (cb) cb.checked = !!note;

            updateGiftUI();
        } catch (e) {
            console.warn('[Custom Print] gift hydrate failed', e);
        }
    }

    // Smallest-tier fallback only — wrap and bundle are priced by the size
    // being wrapped, which SkylitGifts resolves below.
    const CP_GIFT_PRICES = { none: 0, ribbon: 2.49, giftwrap: 3.69, bundle: 4.99 };
    // The gift pills render from the gift product's FIRST variant (smallest
    // tier), so re-stamp them for the size selected now.
    function refreshCpGiftLabels() {
        try {
            if (window.SkylitGifts) window.SkylitGifts.refreshLabels(selectedSize);
        } catch (e) {}
    }
    function updateGiftPriceLine() {
        // currentBasePrice is a money string like "£21.99"; only adjust the
        // display when a gift is selected. Keeps the base untouched.
        refreshCpGiftLabels();
        if (!priceDisplay || !currentBasePrice) return;
        let giftPence = CP_GIFT_PRICES[selectedGift] || 0;
        try {
            if (selectedGift && selectedGift !== 'none' && window.SkylitGifts) {
                const tieredPence = window.SkylitGifts.priceFor(selectedGift, selectedSize);
                if (tieredPence) giftPence = tieredPence / 100;
            }
        } catch (e) {}
        if (giftPence > 0) {
            priceDisplay.innerHTML = `${currentBasePrice} <span style="font-size:12px;color:#666;">+ £${giftPence.toFixed(2)} ${selectedGift}</span>`;
        } else {
            updatePriceDisplay();
        }
    }

    function setupPicker(selector, callback) {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
            el.addEventListener('click', () => {
                if (el.classList.contains('sold-out')) return;

                // Remove active from siblings
                el.parentElement.querySelectorAll('.elem__picker').forEach(e => {
                    e.classList.remove('active__picker');
                });

                // Add active to clicked
                el.classList.add('active__picker');

                // Call callback
                if (callback) callback(el);
            });
        });
    }

    let currentBasePrice = '';

    function updatePrice(priceText) {
        if (priceText) {
            currentBasePrice = priceText;
        }
        updatePriceDisplay();
    }

    function updatePriceDisplay() {
        if (!priceDisplay) return;

        if (upscaleRequested && currentBasePrice) {
            priceDisplay.innerHTML = `${currentBasePrice} <span style="font-size: 12px; color: #666;">+ £2.99 enhancement</span>`;
        } else if (currentBasePrice) {
            priceDisplay.textContent = currentBasePrice;
        }
    }

    function updateFrameVisibility() {
        const frameSection = document.getElementById('frame-section');
        if (!frameSection) return;

        // Check if this is a framed product type
        const isFramed = selectedType.toLowerCase().includes('framed');

        // Show/hide the entire frame section based on product type
        // For Print/Canvas, hide frame section entirely
        // For Framed, show frame section but hide "No Frame" option
        if (isFramed) {
            frameSection.style.display = 'block';
            // Hide "No Frame" option when Framed is selected
            const noFrameOption = frameSection.querySelector('.no-frame-option');
            if (noFrameOption) {
                noFrameOption.style.display = 'none';
                // If "No Frame" was selected, select first available frame instead
                if (noFrameOption.classList.contains('active__picker')) {
                    noFrameOption.classList.remove('active__picker');
                    const firstFrame = frameSection.querySelector('.elem__picker:not(.no-frame-option)');
                    if (firstFrame) {
                        firstFrame.classList.add('active__picker');
                        selectedFrame = firstFrame.dataset.option;
                    }
                }
            }

            // Always apply the currently active frame when Framed is selected
            const activeFrame = frameSection.querySelector('.elem__picker.active__picker:not(.no-frame-option)');
            if (activeFrame) {
                selectedFrame = activeFrame.dataset.option;
                updatePreviewFrame(activeFrame.dataset.frame);
            } else {
                // If no frame is active, select the first one (black)
                const firstFrame = frameSection.querySelector('.elem__picker:not(.no-frame-option)');
                if (firstFrame) {
                    firstFrame.classList.add('active__picker');
                    selectedFrame = firstFrame.dataset.option;
                    updatePreviewFrame(firstFrame.dataset.frame);
                }
            }
        } else {
            // For non-framed products (Print, Canvas), hide the frame section and remove frame
            frameSection.style.display = 'none';
            selectedFrame = ''; // Clear frame selection for non-framed products
            updatePreviewFrame(null); // Remove frame from preview
        }

        // Product type drives which gift options apply (flat Print can't
        // take wrap/bundle) — re-sync the gift UI whenever type changes.
        updateGiftUI();

        // Switching Print <-> Framed resizes .preview-inner (the frame
        // border insets it). renderImage() positions the photo in CSS
        // pixels against the frame's clientWidth/Height, so those values
        // go stale on a frame-type change — re-fit once the new layout
        // settles. Frame *colour* changes keep the same 18px border, so
        // they don't need this.
        if (imageInfo && uploadedImage) {
            requestAnimationFrame(applyPreviewTransforms);
        }
    }

    function updatePreviewFrame(frameClass) {
        if (!previewContainer) return;

        // Remove existing frame classes
        previewContainer.classList.remove('frame-black', 'frame-white', 'frame-natural', 'frame-dark-brown', 'frame-none');

        // Add new frame class
        if (frameClass) {
            previewContainer.classList.add('frame-' + frameClass);
        }
    }

    function updateSizePickerPrices() {
        // Update size picker prices, visibility and sold-out state based on
        // the currently-selected product type AND frame colour.
        //
        // - If no variants exist for the type+size combo at all (e.g.
        //   Canvas doesn't ship in 60×80), hide that size entirely.
        // - If a frame colour is selected, mark sizes that have no variant
        //   for type+size+frame as sold-out (visible but not clickable).
        //   This is what greys out 30×40+ when "Dark Brown" is picked.
        const variants = window.customPrintVariants;
        if (!variants) return;

        const sizeElements = document.querySelectorAll('#size-picker .elem__picker');
        let activeWasHidden = false;
        let firstVisibleElement = null;

        sizeElements.forEach(el => {
            const sizeOption = el.dataset.option;

            const allForSize = variants.filter(v =>
                v.option1 === selectedType && v.option2 === sizeOption
            );

            if (allForSize.length === 0) {
                // No variants for this type+size at all — hide the tile.
                el.style.display = 'none';
                if (el.classList.contains('active__picker')) {
                    activeWasHidden = true;
                    el.classList.remove('active__picker');
                }
                return;
            }

            // If a frame colour is selected, narrow the match further.
            // Note: for Print/Canvas types selectedFrame may be empty/'No
            // Frame', in which case we don't restrict.
            const restrictByFrame = selectedFrame && selectedFrame !== 'No Frame';
            const matching = restrictByFrame
                ? allForSize.filter(v => v.option3 === selectedFrame)
                : allForSize;

            const anyAvailable = matching.some(v => v.available);
            // Prefer an available matching variant for price display; fall
            // back to any matching, then any variant for this type+size.
            const priceVariant = matching.find(v => v.available) || matching[0] || allForSize[0];

            el.style.display = '';
            el.classList.toggle('sold-out', !anyAvailable);
            if (!firstVisibleElement && anyAvailable) firstVisibleElement = el;

            const priceEl = el.querySelector('.variant-price');
            if (priceEl) priceEl.textContent = priceVariant.priceFormatted;
            el.dataset.price = priceVariant.priceFormatted;
        });

        // If the active size was hidden OR is now sold-out, jump to the first
        // available size so the user isn't sitting on an unbuyable selection.
        const activeNow = document.querySelector('#size-picker .elem__picker.active__picker');
        const activeIsSoldOut = activeNow && activeNow.classList.contains('sold-out');
        if ((activeWasHidden || activeIsSoldOut) && firstVisibleElement) {
            if (activeNow) activeNow.classList.remove('active__picker');
            firstVisibleElement.classList.add('active__picker');
            selectedSize = firstVisibleElement.dataset.sizeCode || '30x40';
            updatePrice(firstVisibleElement.dataset.price);
        }

        // Also update the current displayed price if a size is already selected
        const activeSize = document.querySelector('#size-picker .elem__picker.active__picker');
        if (activeSize && activeSize.dataset.price) {
            updatePrice(activeSize.dataset.price);
        }
    }

    // Mark frame colours as sold-out when no variant exists (or none is
    // available) for the currently-selected type + size + this frame.
    // Example: Dark Brown frame is only stocked in A4, so when the user
    // picks 30×40 / 40×50 / 50×70 / 60×80 the Dark Brown swatch greys out
    // and isn't clickable. If the active frame becomes sold-out, jump to
    // the first available frame colour.
    function updateFramePickerAvailability() {
        const variants = window.customPrintVariants;
        if (!variants) return;
        const frameElements = document.querySelectorAll('#frame-picker .elem__picker');
        if (!frameElements.length) return;

        const activeSize = document.querySelector('#size-picker .elem__picker.active__picker');
        const sizeOption = activeSize ? activeSize.dataset.option : null;

        let activeFrameElIsOut = false;
        let firstAvailableFrame = null;

        frameElements.forEach(el => {
            // The "No Frame" option corresponds to Print/Canvas types and
            // doesn't have its own stock dimension — always leave it alone.
            if (el.classList.contains('no-frame-option')) return;

            const frameOption = el.dataset.option;
            const matching = variants.filter(v =>
                v.option1 === selectedType &&
                (!sizeOption || v.option2 === sizeOption) &&
                v.option3 === frameOption
            );
            const anyAvailable = matching.some(v => v.available);
            el.classList.toggle('sold-out', !anyAvailable);
            if (anyAvailable && !firstAvailableFrame) firstAvailableFrame = el;
            if (el.classList.contains('active__picker') && !anyAvailable) activeFrameElIsOut = true;
        });

        // Bump off sold-out frame to first available, so the cart isn't
        // sitting on an unbuyable variant.
        if (activeFrameElIsOut && firstAvailableFrame) {
            frameElements.forEach(e => e.classList.remove('active__picker'));
            firstAvailableFrame.classList.add('active__picker');
            selectedFrame = firstAvailableFrame.dataset.option;
            updatePreviewFrame(firstAvailableFrame.dataset.frame);
        }
    }

    function updateVariantId() {
        // Resolve the variant from type + size + frame. Size MUST take part
        // in the match: variant titles are "Type / Size / Frame", so a
        // type+frame-only scan always lands on that combo's first (A4)
        // variant and every other size gets carted at the A4 price.
        const variantSelect = document.getElementById('product-select');
        if (!variantSelect) return;

        const variants = window.customPrintVariants;
        const activeSize = document.querySelector('#size-picker .active__picker');
        const sizeOption = activeSize ? activeSize.dataset.option : null;
        // Print/Canvas variants carry option3 "No Frame"; selectedFrame is
        // cleared ('') for those types, so derive the frame option from type.
        const frameOption = selectedType === 'Framed' ? selectedFrame : 'No Frame';

        if (variants && variants.length) {
            const match = variants.find(v =>
                (!selectedType || v.option1 === selectedType) &&
                (!sizeOption || v.option2 === sizeOption) &&
                (!frameOption || v.option3 === frameOption)
            );
            if (match) {
                variantSelect.value = match.id;
                document.getElementById('var_id').value = match.id;
            }
            return;
        }

        // Fallback when the variants array is unavailable: title scan,
        // including the size text so it can't resolve to the wrong size.
        for (const option of variantSelect.options) {
            const title = option.textContent.toLowerCase();
            const matchesType = !selectedType || title.includes(selectedType.toLowerCase());
            const matchesSize = !sizeOption || title.includes(sizeOption.toLowerCase());
            const matchesFrame = !frameOption || title.includes(frameOption.toLowerCase());

            if (matchesType && matchesSize && matchesFrame) {
                variantSelect.value = option.value;
                document.getElementById('var_id').value = option.value;
                break;
            }
        }
    }

    // ============================================
    // Add to Cart
    // ============================================
    function setupAddToCart() {
        addToCartBtn.addEventListener('click', submitToCart);
    }

    function updateAddToCartButton() {
        if (uploadedImage && !isPlaceholderImage) {
            addToCartBtn.disabled = false;
            // Show different text when editing an existing order
            const buttonText = isEditMode ? 'Confirm Edits' : 'Add to Cart';
            addToCartBtn.innerHTML = '<span>' + buttonText + '</span>';
        } else {
            addToCartBtn.disabled = true;
            addToCartBtn.innerHTML = isPlaceholderImage
                ? '<span>Upload your image to continue</span>'
                : '<span>Upload an image to continue</span>';
        }
    }

    /**
     * Generate a thumbnail with all transformations applied (rotation, filter, crop, border, personalization)
     * and save to localStorage for cart display
     */
    function generateAndSaveCartThumbnail() {
        return new Promise((resolve) => {
            if (!previewImage || !previewImage.src || !imageInfo) {
                console.warn('[Custom Print] Cannot generate thumbnail: missing previewImage or imageInfo');
                if (window.skylitBeaconThumbnailSkip) {
                    try {
                        window.skylitBeaconThumbnailSkip(orderId, 'custom_print', 'no_custom_frame', {
                            hasPreviewImage: !!previewImage,
                            hasSrc: !!(previewImage && previewImage.src),
                            hasImageInfo: !!imageInfo
                        });
                    } catch (e) {}
                }
                resolve(null);
                return;
            }

            // Use the already-loaded previewImage element directly
            // This avoids CORS issues since it's already loaded in the DOM
            const img = previewImage;

            // Check if image is loaded
            if (!img.complete || img.naturalWidth === 0) {
                console.warn('[Custom Print] Preview image not fully loaded, waiting...');
                img.onload = () => generateThumbnailFromImage(img, resolve);
                img.onerror = () => {
                    console.error('[Custom Print] Failed to load image for thumbnail');
                    if (window.skylitBeaconThumbnailSkip) {
                        try { window.skylitBeaconThumbnailSkip(orderId, 'custom_print', 'generator_error', { error: 'preview_image_load_failed' }); } catch (e) {}
                    }
                    resolve(null);
                };
                return;
            }

            generateThumbnailFromImage(img, resolve);
        });
    }

    function generateThumbnailFromImage(img, resolve) {
        try {
                    // Thumbnail dimensions (match what cart displays)
                    const maxThumbSize = 200;

                    // Determine aspect ratio based on size and orientation
                    const isLandscapeFrame = selectedOrientation === 'landscape';
                    const sizeRatio = SIZE_RATIOS[selectedSize] || SIZE_RATIOS['30x40'];
                    const aspectRatio = isLandscapeFrame ? (1 / sizeRatio) : sizeRatio;

                    console.log('[Custom Print] Thumbnail generation starting:', {
                        selectedOrientation: selectedOrientation,
                        isLandscapeFrame: isLandscapeFrame,
                        selectedSize: selectedSize,
                        sizeRatio: sizeRatio,
                        aspectRatio: aspectRatio,
                        personalization: personalization,
                        cropSettings: cropSettings,
                        imgDimensions: { w: img.naturalWidth, h: img.naturalHeight }
                    });

                    let thumbWidth, thumbHeight;
                    if (isLandscapeFrame) {
                        thumbWidth = maxThumbSize;
                        thumbHeight = Math.round(maxThumbSize / aspectRatio);
                    } else {
                        thumbHeight = maxThumbSize;
                        thumbWidth = Math.round(maxThumbSize * aspectRatio);
                    }

                    // Border settings
                    const hasBorder = personalization.border !== 'none';
                    const borderSize = hasBorder ? 8 : 0; // Scaled down for thumbnail
                    const borderColors = {
                        white: '#ffffff',
                        beige: '#f5f0e6',
                        black: '#0a0a0a',
            'dark-brown': '#1a1310',
                        none: 'transparent'
                    };
                    const borderColor = borderColors[personalization.border] || '#ffffff';

                    // Inner frame dimensions (where the image goes)
                    const innerWidth = thumbWidth - (borderSize * 2);
                    const innerHeight = thumbHeight - (borderSize * 2);

                    // Account for text area at bottom if has personalization
                    const hasText = personalization.headline || personalization.caption;
                    // Only reserve text area for bordered prints - no-border overlays text on image
                    const textAreaHeight = (hasText && hasBorder) ? 20 : 0;
                    const imageAreaHeight = innerHeight - textAreaHeight;

                    const canvas = document.createElement('canvas');
                    canvas.width = thumbWidth;
                    canvas.height = thumbHeight;
                    const ctx = canvas.getContext('2d');

                    // Fill background with border color (or dark if no border)
                    ctx.fillStyle = hasBorder ? borderColor : '#1a1a2e';
                    ctx.fillRect(0, 0, thumbWidth, thumbHeight);

                    // IMPORTANT: Use ORIGINAL image dimensions for sizing (matching CSS behavior)
                    // CSS assigns class based on original, then rotation is applied visually
                    const origWidth = img.naturalWidth;
                    const origHeight = img.naturalHeight;
                    const origRatio = origWidth / origHeight;
                    const isOrigLandscape = origRatio > 1;

                    const rotation = cropSettings.rotation || 0;
                    const scale = (cropSettings.zoom || 100) / 100;

                    // Frame ratio for the image area
                    const frameRatio = innerWidth / imageAreaHeight;
                    const isPortraitFrame = frameRatio < 1;

                    // Calculate display dimensions based on CSS rules (using ORIGINAL ratio)
                    let displayWidth, displayHeight;

                    if (isOrigLandscape && isPortraitFrame) {
                        displayHeight = imageAreaHeight * scale;
                        displayWidth = displayHeight * origRatio;
                    } else if (!isOrigLandscape && !isPortraitFrame) {
                        displayWidth = innerWidth * scale;
                        displayHeight = displayWidth / origRatio;
                    } else if (isOrigLandscape && !isPortraitFrame) {
                        displayHeight = imageAreaHeight * scale;
                        displayWidth = displayHeight * origRatio;
                    } else {
                        displayWidth = innerWidth * scale;
                        displayHeight = displayWidth / origRatio;
                    }

                    // If rotated 90/270, swap visual dimensions for overflow calculation
                    let visualWidth = displayWidth;
                    let visualHeight = displayHeight;
                    if (rotation === 90 || rotation === 270) {
                        [visualWidth, visualHeight] = [visualHeight, visualWidth];
                    }

                    // Calculate offset based on crop settings
                    const overflowX = Math.max(0, (visualWidth - innerWidth) / 2);
                    const overflowY = Math.max(0, (visualHeight - imageAreaHeight) / 2);
                    const offsetX = ((cropSettings.offsetX || 0) / 100) * overflowX;
                    const offsetY = ((cropSettings.offsetY || 0) / 100) * overflowY;

                    // Set clip region for image area
                    ctx.save();
                    ctx.beginPath();
                    ctx.rect(borderSize, borderSize, innerWidth, imageAreaHeight);
                    ctx.clip();

                    // Calculate center of image area
                    const centerX = borderSize + innerWidth / 2 + offsetX;
                    const centerY = borderSize + imageAreaHeight / 2 + offsetY;

                    // Apply transformations
                    ctx.translate(centerX, centerY);
                    ctx.rotate((rotation * Math.PI) / 180);

                    // Apply filter if set
                    if (selectedFilter && selectedFilter !== 'none') {
                        ctx.filter = FILTERS[selectedFilter];
                    }

                    // Draw image (use original display dimensions, rotation handles visual swap)
                    ctx.drawImage(
                        img,
                        -displayWidth / 2,
                        -displayHeight / 2,
                        displayWidth,
                        displayHeight
                    );

                    ctx.restore();

                    // Draw personalization text if present
                    if (hasText) {
                        // Font selection
                        const fontFamily = resolveFontFamily(personalization.font);

                        if (hasBorder) {
                            // With border: text goes in reserved area below image
                            const textAreaY = borderSize + imageAreaHeight;

                            // Fill text area background with border color
                            ctx.fillStyle = borderColor;
                            ctx.fillRect(borderSize, textAreaY, innerWidth, 20);

                            // Determine text color based on border color
                            const textColor = (personalization.border === 'black' || personalization.border === 'dark-brown') ? '#ffffff' : '#0a0a0a';
                            ctx.fillStyle = textColor;
                            ctx.textAlign = 'center';

                            // Draw headline
                            if (personalization.headline) {
                                ctx.font = `600 8px ${fontFamily}`;
                                ctx.fillText(
                                    personalization.headline,
                                    thumbWidth / 2,
                                    textAreaY + (personalization.caption ? 8 : 12)
                                );
                            }

                            // Draw caption
                            if (personalization.caption) {
                                ctx.font = `400 6px ${fontFamily}`;
                                ctx.fillText(
                                    personalization.caption,
                                    thumbWidth / 2,
                                    textAreaY + (personalization.headline ? 16 : 10)
                                );
                            }
                        } else {
                            // No border: overlay text on image with gradient background
                            const gradientHeight = 40; // Gradient overlay height
                            const textBottomPadding = 8;

                            // Draw gradient overlay at bottom of image
                            const gradient = ctx.createLinearGradient(
                                0, thumbHeight - gradientHeight,
                                0, thumbHeight
                            );
                            gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
                            gradient.addColorStop(1, 'rgba(0, 0, 0, 0.5)');
                            ctx.fillStyle = gradient;
                            ctx.fillRect(0, thumbHeight - gradientHeight, thumbWidth, gradientHeight);

                            // Determine text color
                            let textColor;
                            if (personalization.textColor === 'auto' || !personalization.textColor) {
                                textColor = '#ffffff'; // Default to white for overlay on gradient
                            } else {
                                textColor = personalization.textColor === 'white' ? '#ffffff' : '#0a0a0a';
                            }

                            ctx.fillStyle = textColor;
                            ctx.textAlign = 'center';

                            // Add text shadow for better visibility
                            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                            ctx.shadowBlur = 2;
                            ctx.shadowOffsetX = 0;
                            ctx.shadowOffsetY = 1;

                            // Calculate text positions from bottom
                            let currentY = thumbHeight - textBottomPadding;

                            // Draw caption first (bottom)
                            if (personalization.caption) {
                                ctx.font = `400 5px ${fontFamily}`;
                                ctx.fillText(personalization.caption, thumbWidth / 2, currentY);
                                currentY -= 8;
                            }

                            // Draw headline above caption
                            if (personalization.headline) {
                                ctx.font = `600 7px ${fontFamily}`;
                                ctx.fillText(personalization.headline, thumbWidth / 2, currentY);
                            }

                            // Reset shadow
                            ctx.shadowColor = 'transparent';
                            ctx.shadowBlur = 0;
                        }
                    }

                    // Convert to base64 and save to localStorage
                    const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.85);
                    localStorage.setItem(`thumbnail_${orderId}`, thumbnailDataUrl);
                    // Fire-and-forget upload via shared helper (auto-shrinks if over cap).
                    if (window.skylitUploadCartThumbnail) {
                        window.skylitUploadCartThumbnail({ order: orderId, productType: 'custom_print', dataUrl: thumbnailDataUrl });
                    }

                console.log('[Custom Print] Generated cart thumbnail:', {
                    orderId: orderId,
                    canvasSize: { w: canvas.width, h: canvas.height },
                    thumbnailLength: thumbnailDataUrl.length,
                    isPortrait: canvas.height > canvas.width
                });
                resolve(thumbnailDataUrl);
            } catch (err) {
                console.error('[Custom Print] Failed to generate thumbnail:', err);
                // Try to provide more specific error info
                if (err.name === 'SecurityError') {
                    console.error('[Custom Print] CORS error - image may be from different origin');
                }
                if (window.skylitBeaconThumbnailSkip) {
                    try {
                        window.skylitBeaconThumbnailSkip(orderId, 'custom_print', 'generator_error', {
                            error: String(err).slice(0, 200),
                            errorName: err && err.name
                        });
                    } catch (e) {}
                }
                resolve(null);
            }
    }

    async function submitToCart() {
        if (!uploadedImage) {
            shakeUploadZone();
            return;
        }

        // Frame adopted from the cart add-on: keep the two-line structure.
        // Flip the selection back to Print (through the real tile click so
        // #var_id and the _selected_* properties follow) before anything
        // reads the variant; the add-on line is synced before the redirect.
        const cpAddonFrameOwned = cpFrameFromAddon;
        let cpAddonFrameColour = null;
        if (cpAddonFrameOwned && selectedType === 'Framed') {
            cpAddonFrameColour = selectedFrame || 'Black';
            const printTile = document.querySelector('#product-type-picker .elem__picker[data-option="Print"]');
            if (printTile) printTile.click();
        }
        const cpAddonOrderId = orderId;

        // A customer who arrived from the cart's Edit link is not adding
        // anything — the line is already in their cart, and this button reads
        // "Confirm Edits". Saying "Adding to cart" there reads like a second
        // copy is about to appear.
        showLoading(isEditMode ? 'Saving your edits...' : 'Adding to cart...');
        addToCartBtn.classList.add('loading');

        try {
            // Generate and save thumbnail with all transformations for cart display
            await generateAndSaveCartThumbnail();

            // Save data to backend. Flatten crop/personalization to match
            // the admin's /api/custom-print-data/ schema (which reads
            // data.crop_zoom, data.headline, data.text_border, etc.). Nested
            // crop/personalization are kept too for any older readers.
            const saveData = {
                order: orderId,
                size: selectedSize,
                fit_mode: selectedFitMode,
                filter: selectedFilter,
                filter_type: selectedFilter,
                orientation: selectedOrientation,

                // Crop (flat — what skylit-admin expects)
                crop_zoom: cropSettings.zoom,
                crop_rotation: cropSettings.rotation,
                crop_offset_x: cropSettings.offsetX,
                crop_offset_y: cropSettings.offsetY,
                crop: cropSettings,

                // Personalization (flat — admin schema)
                headline: personalization.headline || '',
                caption: personalization.caption || '',
                font: personalization.font || '',
                text_border: personalization.border || '',
                text_color: personalization.textColor || '',
                personalization: personalization,

                // Image metadata so admin can show / validate
                image_width: imageInfo?.width ?? null,
                image_height: imageInfo?.height ?? null,
                image_dpi: imageInfo?.dpi ?? null,
                image_format: imageInfo?.format ?? null,
                file_size_bytes: imageInfo?.file_size_bytes ?? null,
                original_filename: imageInfo?.original_filename ?? null,

                _selected_type: selectedType,
                _selected_frame: selectedFrame,
                // Authoring-device snapshot for admin-side ad/reporting segmentation.
                // Server parses userAgentData (Client Hints) first, falls back to UA regex.
                device: (function(){
                    var uad = null;
                    try {
                        if (navigator.userAgentData) {
                            uad = {
                                brands: navigator.userAgentData.brands || null,
                                mobile: !!navigator.userAgentData.mobile,
                                platform: navigator.userAgentData.platform || ''
                            };
                        }
                    } catch (e) {}
                    return {
                        userAgent: (navigator && navigator.userAgent) || '',
                        userAgentData: uad,
                        viewport: { w: window.innerWidth, h: window.innerHeight }
                    };
                })()
            };

            await fetch(API_BASE + 'api/custom-print-data/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(saveData)
            });

            // Server-side room-scene cart thumbnail, same as the other five
            // customizers. The client-side canvas thumbnail above is still
            // written to localStorage and still shown instantly; this replaces
            // it once it lands, so a custom print sits in a framed scene in the
            // cart instead of falling back to the product listing photo. It is
            // triggered inside the custom-print-data save above, which this
            // awaits. It used to be a separate
            // fire-and-forget POST from here, which raced the navigation to
            // /cart and was sometimes dropped before it left the device even
            // with keepalive set. The save is awaited, so it cannot be lost.

            // Build cart data — re-resolve the variant from the current
            // selections right before the read, so the cart can never get a
            // variant that drifted from what the customer configured.
            updateVariantId();
            const variantId = document.getElementById('var_id').value;

            // Format filter name for display
            const filterDisplay = selectedFilter === 'none' ? 'Original' :
                selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1);

            // Format font name for display
            const fontDisplay = personalization.font.charAt(0).toUpperCase() + personalization.font.slice(1);

            // Format border for display. Hyphenated values like 'dark-brown'
            // become 'Dark Brown', not 'Dark-brown'.
            const borderDisplay = personalization.border === 'none' ? 'No Border' :
                personalization.border
                    .split('-')
                    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(' ');

            // Format orientation for display
            const orientationDisplay = selectedOrientation.charAt(0).toUpperCase() + selectedOrientation.slice(1);

            // One fresh copy reference per add-to-cart, shared by the print and
            // its paired add-on so cart/drawer controls bind to this exact copy.
            const cpLineRef = window.SkylitPairing
                ? window.SkylitPairing.makeLineRef()
                : orderId;

            // Build cart properties
            const cartProperties = {
                _OrderID: orderId,
                // Copy reference tying this print to its own gift add-on.
                // _OrderID is shared between copies of one design.
                _cart_line_ref: cpLineRef,
                'Image': `Uploaded: ${imageInfo.width}x${imageInfo.height}px`,
                'Filter': filterDisplay,
                'Orientation': orientationDisplay,
                _print_url: API_BASE + 'api/download/' + orderId,
                // The room-scene thumbnail. api/download/ used to be pointed at
                // here, but that is the finished print file, which only exists
                // after the order is paid and rendered — at cart time it 404s
                // and the row fell back to the generic product image.
                _thumbnail_final: API_BASE + 'api/cart-thumbnail-img/' + orderId,
                // The bare print. Its presence is what makes the cart draw the
                // scene LIVE in CSS (snippets/cart-scene-thumb.liquid) instead
                // of showing the baked composite, so a frame bought from the
                // cart's own upsell appears without re-rendering anything.
                _thumbnail_print: API_BASE + 'api/cart-print-img/' + orderId,
                _selected_type: selectedType,
                _selected_size: selectedSize,
                _selected_frame: selectedFrame,
                _filter: selectedFilter,
                _orientation: selectedOrientation,
                _crop_zoom: cropSettings.zoom,
                _crop_rotation: cropSettings.rotation,
                _crop_offset_x: cropSettings.offsetX,
                _crop_offset_y: cropSettings.offsetY,
                _font: personalization.font,
                _text_border: personalization.border,
                _original_image_url: originalImageUrl || ''
            };

            // Always show border selection
            cartProperties['Border'] = borderDisplay;

            // Add personalization fields if they have values
            if (personalization.headline) {
                cartProperties['Headline'] = personalization.headline;
                cartProperties['_headline'] = personalization.headline;
            }
            if (personalization.caption) {
                cartProperties['Caption'] = personalization.caption;
                cartProperties['_caption'] = personalization.caption;
            }
            if (personalization.headline || personalization.caption) {
                cartProperties['Font'] = fontDisplay;

                // Add text color when border is 'none'
                if (personalization.border === 'none') {
                    // Determine actual text color (resolve 'auto' to actual color)
                    let actualTextColor = personalization.textColor;
                    if (actualTextColor === 'auto') {
                        const imgEl = document.getElementById('preview-image');
                        actualTextColor = detectImageBrightness(imgEl);
                    }
                    const textColorDisplay = actualTextColor.charAt(0).toUpperCase() + actualTextColor.slice(1);
                    cartProperties['Text Color'] = textColorDisplay;
                    cartProperties['_text_color'] = actualTextColor;
                }
            }

            if (window.SkylitGalleryWall) window.SkylitGalleryWall.applyProperties(cartProperties);

            const cartData = {
                items: [{
                    id: parseInt(variantId),
                    quantity: 1,
                    properties: cartProperties
                }]
            };

            // Paired gift add-on — same per-line model as the other
            // customizers. Gift note + design + font ride on the add-on's
            // properties; _paired_with_order_id ties it to this print.
            if (selectedGift !== 'none' && CP_GIFT_IDS[selectedGift]) {
                const giftDom = readGiftDom();
                const giftProps = { _paired_with_order_id: orderId, _paired_with_line_ref: cpLineRef };
                if (selectedGift === 'ribbon' || selectedGift === 'bundle') {
                    const cpRibbon = window.SkylitGifts ? window.SkylitGifts.ribbonColorKey(selectedRibbon) : (selectedRibbon || 'Red');
                    if (window.SkylitGifts) window.SkylitGifts.rememberRibbon('cp_' + orderId, cpRibbon);
                    giftProps['Ribbon Colour'] = cpRibbon;
                    giftProps._ribbon_color = cpRibbon;
                }
                if (selectedGift === 'giftwrap' || selectedGift === 'bundle') {
                    const paperKey = window.SkylitGifts ? window.SkylitGifts.designKey(selectedPaper) : 'regular';
                    giftProps['Gift Wrap Theme'] = window.SkylitGifts ? window.SkylitGifts.labelFor(paperKey) : 'Regular';
                    giftProps._gift_wrap_theme = paperKey;
                }
                if ((selectedGift === 'ribbon' || selectedGift === 'bundle') &&
                    giftDom.enabled && giftDom.note.trim()) {
                    giftProps._gift_note = giftDom.note;
                    giftProps._gift_card_design = giftDom.design;
                    giftProps._gift_card_font = giftDom.font;
                }
                // Wrap/bundle are size-tiered by the print; CP_GIFT_IDS
                // (smallest tier) is the fallback when the map is missing.
                let cpGiftId = CP_GIFT_IDS[selectedGift];
                try {
                    const tiered = window.SkylitGifts && window.SkylitGifts.variantFor(selectedGift, selectedSize, selectedPaper);
                    if (tiered) cpGiftId = tiered;
                } catch (e) {}
                cartData.items.push({
                    id: cpGiftId,
                    quantity: 1,
                    properties: giftProps
                });
            }

            // Re-fetch cart to get current state (in case it changed)
            // IMPORTANT: Only track PRINT items, not enhancement services
            let currentCartOrderIds = [];
            let currentCartKeys = [];
            try {
                const freshCart = await fetch('/cart.js?t=' + Date.now());
                const freshCartData = await freshCart.json();
                freshCartData.items.forEach((item) => {
                    const itemOrderId = item.properties?._OrderID || item.properties?.OrderID;
                    if (itemOrderId) {
                        // Skip enhancement service - we only want to track print items
                        const title = (item.product_title || item.title || '').toLowerCase();
                        const isEnhancement = title.includes('enhancement') || title.includes('upscale');
                        if (!isEnhancement) {
                            currentCartOrderIds.push(itemOrderId);
                            currentCartKeys.push(item.key);
                            console.log('[Custom Print] Found print item:', itemOrderId, 'key:', item.key);
                        } else {
                            console.log('[Custom Print] Skipping enhancement service:', itemOrderId);
                        }
                    }
                });
                console.log('[Custom Print] Fresh cart scan (prints only) - OrderIDs:', currentCartOrderIds, 'Keys:', currentCartKeys);
            } catch (e) {
                console.error('[Custom Print] Failed to fetch fresh cart:', e);
                // Fall back to cached values
                currentCartOrderIds = cartOrderIdPresent;
                currentCartKeys = cartOrderIdKeys;
            }

            // Check if this is an edit (order already in cart)
            const orderIndex = currentCartOrderIds.indexOf(orderId);
            const isOrderInCart = orderIndex !== -1;

            console.log('[Custom Print] Cart submit check:', {
                orderId: orderId,
                isEditMode: isEditMode,
                isOrderInCart: isOrderInCart,
                orderIndex: orderIndex,
                currentCartOrderIds: currentCartOrderIds,
                currentCartKeys: currentCartKeys
            });

            if (window.SkylitPairing) {
                // Drop anything this design already owns (print + paired
                // ribbon/wrap/bundle/wall strips), driven by a LIVE cart read.
                // Enhancement/upscale lines pair by _OrderID and are purged too,
                // then re-added below when still requested.
                await window.SkylitPairing.purgeDesign(orderId);
            } else if (isOrderInCart) {
                // Helper missing (asset blocked) — old inline path.
                const cartKeyToRemove = currentCartKeys[orderIndex];
                console.log('[Custom Print] Updating existing cart item, removing key:', cartKeyToRemove);

                // Raise the edit-in-flight flag so cart-attrs-sync skips orphan
                // reap during the remove-old + add-new gap.
                window.__skylitCartEditInFlight = true;

                // Remove old item and wait for confirmation
                const removeResponse = await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id: cartKeyToRemove,
                        quantity: 0
                    })
                });

                const removeResult = await removeResponse.json();
                console.log('[Custom Print] Remove response:', removeResponse.status, removeResult);

                if (!removeResponse.ok) {
                    console.error('[Custom Print] Failed to remove old item');
                } else {
                    console.log('[Custom Print] Old item removed successfully');
                }

                // Also remove any associated enhancement service AND any
                // paired gift add-on (ribbon/wrap/bundle) for this OrderID
                // — otherwise switching wrapping mid-edit duplicates the
                // add-on line.
                try {
                    const cartAfterRemove = await fetch('/cart.js?t=' + Date.now());
                    const cartAfterRemoveData = await cartAfterRemove.json();
                    for (const item of cartAfterRemoveData.items) {
                        const props = item.properties || {};
                        const itemOrderId = props._OrderID;
                        const title = (item.product_title || item.title || '').toLowerCase();
                        const isEnhancement = title.includes('enhancement') || title.includes('upscale');
                        const isPairedAddon = props._paired_with_order_id === orderId;

                        if ((itemOrderId === orderId && isEnhancement) || isPairedAddon) {
                            console.log('[Custom Print] Removing associated add-on:', item.key);
                            const addonRemove = await fetch('/cart/change.js', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ id: item.key, quantity: 0 })
                            });
                            console.log('[Custom Print] Add-on remove response:', addonRemove.status);
                        }
                    }
                } catch (e) {
                    console.error('[Custom Print] Error checking for associated add-ons:', e);
                }

                console.log('[Custom Print] Adding updated item');
            }

            // Add to Shopify cart
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(cartData)
            });

            if (response.ok) {
                // Add upscale service if requested (before generating new order ID)
                if (upscaleRequested) {
                    await addUpscaleServiceToCart();
                    upscaleRequested = false; // Reset for next order
                }

                // Pair with the edit_personalisations_opened event so the
                // activity log shows whether a customer who entered edit
                // mode actually confirmed the changes.
                if (isEditMode) {
                    try {
                        fetch(API_BASE + 'api/order-event', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                order_id: orderId,
                                event: 'edit_personalisations_confirmed',
                                detail: window.location.href.slice(0, 200)
                            })
                        }).catch(function() {});
                    } catch (e) {}
                }

                // Generate new order ID for next order (prevents accidental edits)
                orderId = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
                // Sync the frame add-on line to the confirmed selection (or
                // remove it if the frame was deselected), then redirect.
                if (cpAddonFrameOwned && window.SkylitPairing && window.SkylitPairing.syncFrameAddon) {
                    await window.SkylitPairing.syncFrameAddon(cpAddonOrderId, cpAddonFrameColour);
                }
                // Redirect to cart
                window.location.href = window.SkylitGalleryWall ? window.SkylitGalleryWall.destination() : '/cart';
            } else {
                throw new Error('Failed to add to cart');
            }

        } catch (error) {
            console.error('[Custom Print] Cart error:', error);
            hideLoading();
            addToCartBtn.classList.remove('loading');
            showError('Failed to add to cart. Please try again.');
        }
    }

    function shakeUploadZone() {
        uploadZone.style.animation = 'shake 0.5s';
        setTimeout(() => {
            uploadZone.style.animation = '';
        }, 500);
    }

    // ============================================
    // UI Helpers
    // ============================================
    function showLoading(text) {
        const loadingText = loadingOverlay.querySelector('.loading-text');
        if (loadingText) loadingText.textContent = text || 'Loading...';
        loadingOverlay.classList.add('show');
    }

    function hideLoading() {
        loadingOverlay.classList.remove('show');
    }

    function showError(message) {
        alert(message); // Simple alert for now, could be replaced with toast
    }

    // ============================================
    // Start
    // ============================================
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();

// ============================================
// TOOLTIP TAP-TO-TOGGLE
// Hover-only tooltips flash and vanish on phones: the tap applies WebKit's
// simulated :hover, but the same tap clicks the parent tile, whose handler
// mutates the DOM — and WebKit drops simulated hover on DOM mutation.
// Tapping the "?" now toggles .tooltip-open instead. Capture phase so
// stopPropagation runs BEFORE the delegated tile-select handlers — a tap
// on the "?" must open the tooltip, not also select/deselect the tile.
// Mirrors the family-moon fix in hor-moon-myjs.js.
// ============================================
document.addEventListener('click', function (e) {
    var icon = e.target.closest ? e.target.closest('.tooltip-icon') : null;
    var open = document.querySelectorAll('.tooltip-icon.tooltip-open');
    var i;
    if (!icon) {
        for (i = 0; i < open.length; i++) open[i].classList.remove('tooltip-open');
        return;
    }
    e.stopPropagation();
    var wasOpen = icon.classList.contains('tooltip-open');
    for (i = 0; i < open.length; i++) open[i].classList.remove('tooltip-open');
    if (!wasOpen) icon.classList.add('tooltip-open');
}, true);
